const CUSTOM_PRODUCT_BUNDLED = 'CUSTOM-PRODUCT-BUNDLED';

class CustomProductA11y {
  constructor({ root } = {}) {
    this.root = root || document;
  }
  setExpandedState(expanded, isExpanded) {
    expanded.setAttribute('aria-expanded', String(isExpanded));
    const toggleButton = expanded.querySelector('button');
    if (!toggleButton) return;
    toggleButton.setAttribute('aria-expanded', String(isExpanded));
    const controls = expanded.getAttribute('aria-controls');
    if (controls) {
      toggleButton.setAttribute('aria-controls', controls);
    }
  }
  moveFocusOut(content, expanded) {
    if (!content.contains(document.activeElement)) return;
    const target = expanded.querySelector('button') || expanded;
    target?.focus?.({ preventScroll: true });
    if (content.contains(document.activeElement)) {
      document.activeElement.blur?.();
    }
  }
  syncDisclosures(root = this.root) {
    const expandedControls = root.querySelectorAll('.legend-expanded[aria-expanded]');
    expandedControls.forEach((expanded) => {
      const content = expanded.closest('[expanded-parent]')?.querySelector('[expanded-content]');
      if (!content) return;
      const isExpanded = expanded.getAttribute('aria-expanded') === 'true';
      this.setExpandedState(expanded, isExpanded);
      if (!isExpanded) {
        this.moveFocusOut(content, expanded);
      }
      content.hidden = !isExpanded;
    });
  }
  toggleDisclosure(expanded, expandedState, onTransitionEnd) {
    const currentState = expanded.getAttribute('aria-expanded') === 'true';
    const nextState = expandedState ?? !currentState;
    const content = expanded.closest('[expanded-parent]')?.querySelector('[expanded-content]');
    if (!content || currentState === nextState) return;
    if (nextState) {
      content.hidden = false;
    }
    const height = nextState ? ['0px', `${content.scrollHeight}px`] : [`${content.scrollHeight}px`, '0px'];
    content.style.overflow = 'hidden';
    Motion.animate(
      content,
      { height },
      { duration: 0.25, easing: 'ease' }
    ).finished.then(() => {
      if (!nextState) {
        this.moveFocusOut(content, expanded);
        content.hidden = true;
        content.style.height = '';
        content.style.overflow = '';
        onTransitionEnd?.();
        return;
      }
      content.style.height = 'auto';
      content.style.overflow = 'initial';
      onTransitionEnd?.();
    });
    this.setExpandedState(expanded, nextState);
  }
  focusFieldsetToggle(fieldset) {
    requestAnimationFrame(() => {
      const nextFocus = fieldset.querySelector('legend > .legend-expanded .legend-icon')
        || fieldset.querySelector('legend > .legend-expanded button')
        || fieldset.querySelector('legend > .legend-expanded');
      nextFocus?.focus({ preventScroll: true });
    });
  }
  static syncGroupedRadios(container) {
    const groups = container.querySelectorAll('.field-bundle__section, .field-bundle__item');
    for (const group of groups) {
      const expanded = group.querySelector(':scope > .legend-expanded[aria-expanded]');
      const isExpanded = expanded?.getAttribute('aria-expanded') === 'true';
      const content = Array.from(group.children).find((element) => element.hasAttribute('expanded-content'));
      const labels = Array.from(content?.querySelectorAll('.ctm-field-btn') || []).filter((label) => {
        if (label.closest('[expanded-parent]') !== group) return false;
        const radio = label.querySelector('input[type="radio"]');
        return radio && !radio.disabled;
      });
      if (!labels.length) continue;
      if (!isExpanded) {
        labels.forEach((label) => {
          label.tabIndex = -1;
          label.setAttribute('aria-checked', String(label.querySelector('input[type="radio"]')?.checked || false));
        });
        continue;
      }
      const checkedLabel = labels.find((label) => label.querySelector('input[type="radio"]')?.checked);
      const tabStop = checkedLabel || labels[0];
      labels.forEach((label) => {
        const isTabStop = label === tabStop;
        label.tabIndex = isTabStop ? 0 : -1;
        label.setAttribute('aria-checked', String(label.querySelector('input[type="radio"]')?.checked || false));
      });
    }
  }
  static handleGroupedRadioKeydown(event) {
    const keys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Enter', ' '];
    if (!keys.includes(event.key)) return false;
    const label = event.target.closest('.ctm-field-btn');
    if (!label) return false;
    const group = label.closest('.field-bundle__item, .field-bundle__section');
    if (!group) return false;
    const content = Array.from(group.children).find((element) => element.hasAttribute('expanded-content'));
    const labels = Array.from(content?.querySelectorAll('.ctm-field-btn') || []).filter((item) => {
      if (item.closest('[expanded-parent]') !== group) return false;
      const radio = item.querySelector('input[type="radio"]');
      return radio && !radio.disabled;
    });
    const currentIndex = labels.indexOf(label);
    if (currentIndex < 0) return false;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const radio = label.querySelector('input[type="radio"]');
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }
    if (labels.length < 2) return false;
    let nextIndex = currentIndex;
    if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + labels.length) % labels.length;
    }
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % labels.length;
    }
    if (nextIndex === currentIndex) return false;
    event.preventDefault();
    const nextLabel = labels[nextIndex];
    const nextRadio = nextLabel.querySelector('input[type="radio"]');
    nextRadio.checked = true;
    nextLabel.focus();
    nextRadio.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }
  getEnabledOptions(selectEl) {
    return selectEl.options.filter((option) => (
      option.getAttribute('aria-disabled') !== 'true' &&
      option.getAttribute('aria-hidden') !== 'true'
    ));
  }
  getSelectedOption(selectEl) {
    return selectEl.options.find((option) => option.classList.contains('tfs__option--selected'));
  }
  getActiveOption(selectEl) {
    return selectEl.optionsContainer.querySelector('.tfs__option--active') || this.getSelectedOption(selectEl);
  }
  syncCombobox(selectEl) {
    selectEl.options.forEach((option) => {
      option.setAttribute('aria-selected', String(option.classList.contains('tfs__option--selected')));
    });
    this.setActiveOption(selectEl, this.getSelectedOption(selectEl) || this.getEnabledOptions(selectEl)[0]);
  }
  openCombobox(selectEl) {
    selectEl.optionsContainer.style.display = 'block';
    selectEl.input.setAttribute('aria-expanded', 'true');
    this.setActiveOption(selectEl, this.getSelectedOption(selectEl) || this.getEnabledOptions(selectEl)[0]);
    setTimeout(() => {
      selectEl.optionsContainer.style.height = `${selectEl.optionsHeight}px`;
    });
  }
  closeCombobox(selectEl) {
    selectEl.input.setAttribute('aria-expanded', 'false');
    selectEl.optionsContainer.style.height = '0';
    setTimeout(() => {
      selectEl.optionsContainer.style.display = 'none';
    }, 300);
  }
  setActiveOption(selectEl, option) {
    if (!option) return;
    selectEl.optionsContainer.querySelector('.tfs__option--active')?.classList.remove('tfs__option--active');
    option.classList.add('tfs__option--active');
    selectEl.activeIndex = this.getEnabledOptions(selectEl).indexOf(option);
    if (option.id) {
      selectEl.input.setAttribute('aria-activedescendant', option.id);
    }
    option.scrollIntoView({ block: 'nearest' });
  }
  selectComboboxOption(selectEl, option, onSelect) {
    if (!option || option.getAttribute('aria-disabled') === 'true' || option.getAttribute('aria-hidden') === 'true') return;
    if (option.classList.contains('tfs__option--selected')) {
      this.closeCombobox(selectEl);
      return;
    }
    selectEl.optionsContainer.querySelector('.tfs__option--selected')?.classList.remove('tfs__option--selected');
    selectEl.options.forEach((item) => item.setAttribute('aria-selected', 'false'));
    option.classList.add('tfs__option--selected');
    option.setAttribute('aria-selected', 'true');
    this.setActiveOption(selectEl, option);
    selectEl.value = selectEl.value.map(({ value }) => value).join(',');
    onSelect?.(option);
    this.closeCombobox(selectEl);
  }
  moveActiveOption(selectEl, offset) {
    const options = this.getEnabledOptions(selectEl);
    if (!options.length) return;
    const currentOption = this.getActiveOption(selectEl);
    const currentIndex = options.indexOf(currentOption);
    const nextIndex = currentIndex < 0 ? 0 : (currentIndex + offset + options.length) % options.length;
    this.setActiveOption(selectEl, options[nextIndex]);
  }
  handleComboboxKeydown(selectEl, event, onSelect) {
    const activeOption = this.getActiveOption(selectEl);
    const isOpen = selectEl.input.getAttribute('aria-expanded') === 'true';
    if (event.key === 'Tab') {
      this.closeCombobox(selectEl);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!isOpen) {
        this.openCombobox(selectEl);
        return;
      }
      this.moveActiveOption(selectEl, 1);
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        this.openCombobox(selectEl);
        return;
      }
      this.moveActiveOption(selectEl, -1);
      return;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      this.openCombobox(selectEl);
      this.setActiveOption(selectEl, this.getEnabledOptions(selectEl)[0]);
      return;
    }
    if (event.key === 'End') {
      event.preventDefault();
      this.openCombobox(selectEl);
      const options = this.getEnabledOptions(selectEl);
      this.setActiveOption(selectEl, options[options.length - 1]);
      return;
    }
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!isOpen) {
        this.openCombobox(selectEl);
        return;
      }
      this.selectComboboxOption(selectEl, activeOption, onSelect);
      return;
    }
    if (event.key === 'Escape') {
      if (!isOpen) return;
      event.preventDefault();
      this.closeCombobox(selectEl);
      selectEl.input.focus();
    }
  }
  setupDateField(field) {
    const input = field.input;
    CustomProductA11y.dateFieldCount = (CustomProductA11y.dateFieldCount || 0) + 1;
    const baseIdentifier = input.id || input.name || 'tps-field-date';
    let identifier = baseIdentifier;
    if (!input.id || document.getElementById(input.id) !== input) {
      identifier = `${baseIdentifier}-${CustomProductA11y.dateFieldCount}`;
      while (document.getElementById(identifier)) {
        CustomProductA11y.dateFieldCount += 1;
        identifier = `${baseIdentifier}-${CustomProductA11y.dateFieldCount}`;
      }
      input.id = identifier;
    }
    field.calendarId = `${identifier}-calendar`;
    field.errorId = `${identifier}-error`;
    field.error = null;
    field.monthControlHandlers = [];
  }
  connectDatePicker(field, picker) {
    const calendar = picker.calendarContainer;
    if (!calendar) return;
    this.setDateExpanded(field, false);
    calendar.id = field.calendarId;
    calendar.classList.add('tps-field-date-calendar');
    calendar.setAttribute('role', 'dialog');
    calendar.setAttribute('aria-label', `${field.input.getAttribute('aria-label')} calendar`);
    field.input.setAttribute('aria-controls', field.calendarId);
    this.enhanceDateMonthControl(field, picker.prevMonthNav, 'Previous month');
    this.enhanceDateMonthControl(field, picker.nextMonthNav, 'Next month');
  }
  enhanceDateMonthControl(field, control, label) {
    if (!control) return;
    control.setAttribute('role', 'button');
    control.setAttribute('aria-label', label);
    const handleKeydown = (event) => {
      if (!['Enter', ' '].includes(event.key) || control.getAttribute('aria-disabled') === 'true') return;
      event.preventDefault();
      event.stopPropagation();
      control.click();
    };
    control.addEventListener('keydown', handleKeydown);
    field.monthControlHandlers.push({ control, handleKeydown });
  }
  setDateExpanded(field, expanded) {
    field.input.setAttribute('aria-expanded', `${expanded}`);
  }
  showDateError(field, text) {
    this.clearDateError(field);
    field.error = document.createElement('div');
    field.error.id = field.errorId;
    field.error.className = 'variant-error-tip';
    field.error.setAttribute('role', 'alert');
    field.error.textContent = text;
    field.closest('div').appendChild(field.error);
    const descriptions = new Set((field.input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    descriptions.add(field.errorId);
    field.input.setAttribute('aria-describedby', [...descriptions].join(' '));
    field.input.setAttribute('aria-invalid', 'true');
  }
  clearDateError(field) {
    field.error?.remove();
    field.error = null;
    const descriptions = (field.input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== field.errorId);
    if (descriptions.length) {
      field.input.setAttribute('aria-describedby', descriptions.join(' '));
    } else {
      field.input.removeAttribute('aria-describedby');
    }
    field.input.setAttribute('aria-invalid', 'false');
  }
  showInlineError(input, container, errorId, text) {
    this.clearInlineError(input, container, errorId);
    const error = document.createElement('div');
    error.id = errorId;
    error.className = 'variant-error-tip';
    error.setAttribute('role', 'alert');
    error.textContent = text;
    container.appendChild(error);
    const descriptions = new Set((input.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));
    descriptions.add(errorId);
    input.setAttribute('aria-describedby', [...descriptions].join(' '));
    input.setAttribute('aria-invalid', 'true');
    return error;
  }
  clearInlineError(input, container, errorId) {
    Array.from(container.querySelectorAll('.variant-error-tip')).find((error) => error.id === errorId)?.remove();
    const descriptions = (input.getAttribute('aria-describedby') || '')
      .split(/\s+/)
      .filter((id) => id && id !== errorId);
    if (descriptions.length) {
      input.setAttribute('aria-describedby', descriptions.join(' '));
    } else {
      input.removeAttribute('aria-describedby');
    }
    input.setAttribute('aria-invalid', 'false');
  }
  destroyDateField(field) {
    this.setDateExpanded(field, false);
    for (const { control, handleKeydown } of field.monthControlHandlers) {
      control.removeEventListener('keydown', handleKeydown);
    }
    field.monthControlHandlers = [];
  }
}

class CustomProductParent extends HTMLElement {
  constructor() {
    super();
    this.productCache = new Map();
    this.productInfoRequestId = 0;
    this.bundleProductRequestId = 0;
    this.mediaRevision = 0;
    this.abortController = null;
    this.assembledFields = [];
    this.attrImagePreviews = {
      timer: null,
      currentNode: null,
      clickController: null,
      width: 248,
      rightWidth: 248,
      isHoverDevice: window.matchMedia('(hover: hover) and (pointer: fine)').matches
    }
    this.a11y = new CustomProductA11y({ root: this });
    this.api_url = 'https://s7d6.scene7.com/ir/render/TWOPAGESCURTAINS/';
    this.adobe_keys = ['color', 'trim-border-style', 'leading-edge-border-color', 'bottom-border-color'];
  }
  get defaultOptions() {
    const textContent = this.querySelector(`script[type="application/json"][data-selected-variant]`)?.textContent;
    return textContent ? JSON.parse(textContent) : {};
  }
  get skeleton() {
    return this.querySelector('.custom-field-skeleton');
  }
  get skuImage() {
    return this.querySelectorAll('.image-swatch[data-preview-image]');
  }
  get quantityInput() {
    return this.querySelector('custom-quantity-input');
  }
  get addToCartForm() {
    return this.querySelector('.add-to-cart-form');
  }
  get addToCartButton() {
    return this.addToCartForm?.querySelector('button.custom-add-to-cart');
  }
  get selectedKeys() {
    return this.assembledFields.filter(item => !item.hide).reduce((pre, cur) => {
      return pre.concat(cur.values?.filter((value) => value.selected))
    }, []).filter((item) => item?.key);
  }
  get addToCartFormRect() {
    return this.addToCartForm.getBoundingClientRect();
  }
  get proudctImageRect() {
    return this.querySelector('.ctm-product_left').getBoundingClientRect();
  }
  get productBtnReact() {
    return this.querySelector('.add-to-cart-form').getBoundingClientRect();
  }
  encrypt(num, times = 100) {
    const base = Math.round(num * times) + 622152;
    return base * 10 + 7;
  }
  handleize(name) {
    if (!name || typeof name !== 'string') {
      return '';
    }
    return name.toLowerCase().trim().replace(/\s+/g, '-').replace(/[^a-z0-9\-]/g, '').replace(/-+/g, '-').replace(/^-+|-+$/g, '');
  }
  formatCartDateValue(value) {
    const rawValue = `${value || ''}`.trim();
    const parts = rawValue.split('/');
    if (parts.length !== 3) return rawValue;
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }
  priceFromCents(cents) {
    const str = String(Math.round(cents)).padStart(3, '0');
    return str.slice(0, -2) + '.' + str.slice(-2);
  }
  checkEdgePrice(list, value) {
    if (list.type === 'range') {
      return value >= list.min && value <= list.max;
    }
    if (list.type === 'exact') {
      return value === list.value;
    }
    return false;
  }
  computedPrice(priceRule = {}) {
    if (priceRule.ruleType === 'fixed') {
      return priceRule.amount * 100;
    } else if (priceRule.ruleType === 'percentage') {
      return this.defaultOptions.price * priceRule.rate;
    }
    return 0;
  }
  computedEdgePrice(lists, type, value) {
    const list = lists.find((list) => list.edgeType === type && this.checkEdgePrice(list, value));
    if (!list) return 0;
    if (list.type == 'range') {
      const { min, stepSize, stepStart, stepPrice, price } = list;
      let totalPrice = 0;
      const isValidStepStart = Number.isFinite(stepStart) && stepStart >= 0;
      if (isValidStepStart) {
        totalPrice = stepStart * 100;
        let step = Math.ceil((value - min) / stepSize);
        totalPrice += step * stepPrice * 100;
      } else {
        totalPrice = price * 100;
      }
      return totalPrice;
    }
    if (list.type == 'exact') {
      return list.price * 100;
    }
    return 0;
  }
  computedSizePrice(option = {}) {
    const { pricingRule = {}, tps_width_inches, tps_length_inches } = option;
    if (!tps_width_inches || !tps_length_inches) return 0;
    const { dimension, tiers = [] } = pricingRule;
    const width = tps_width_inches * 1;
    const length = tps_length_inches * 1;
    const area = width * length;
    if (dimension === "edge") {
      const fieldWidthPrice = this.computedEdgePrice(tiers, 'width', width);
      const fieldLengthPrice = this.computedEdgePrice(tiers, 'length', length);
      return fieldWidthPrice + fieldLengthPrice;
    }
    if (dimension === "area") {
      return this.computedEdgePrice(tiers, '', area);
    }
    return 0;
  }
  createSizeTitle(option) {
    const { tps_width_value, tps_length_value, tps_width_fraction, tps_length_fraction, tps_size_unit } = option;
    if (!tps_width_value || !tps_length_value) return '';
    let width_fraction = '';
    let length_fraction = '';
    if (tps_width_fraction && !['-/-', '0/0'].includes(tps_width_fraction)) {
      width_fraction = ` ${tps_width_fraction}`;
    }
    if (tps_length_fraction && !['-/-', '0/0'].includes(tps_length_fraction)) {
      length_fraction = ` ${tps_length_fraction}`;
    }
    let width = `${tps_width_value}${width_fraction}"W`;
    let length = `${tps_length_value}${length_fraction}"L`;
    if (tps_size_unit === 'cm') {
      width = `${tps_width_value}cm W`;
      length = `${tps_length_value}cm L`;
    }
    return `${width} x ${length}`;
  }
  computedNumberPrice(option = {}) {
    const { pricingRule = {}, current_value } = option;
    if (!current_value) return 0;
    const { tiers = [] } = pricingRule;
    const value = current_value * 1;
    return this.computedEdgePrice(tiers, '', value);
  }
  bindClickClose() {
    if ('AbortController' in window) {
      this.attrImagePreviews.clickController?.abort();
      this.attrImagePreviews.clickController = new AbortController();
      document.addEventListener('click', this.onClickOutside.bind(this), { signal: this.attrImagePreviews.clickController.signal });
    } else {
      document.addEventListener('click', this.onClickOutside.bind(this));
    }
  }
  onClickOutside(e) {
    const wrapper = this.attrImagePreviews.currentNode.closest('.image-swatch-wrapper');
    if (e.target.closest('.attr-image-preview') || wrapper.contains(e.target)) {
      return;
    }
    this.removePreviewImage();
    this.unbindClickOutside();
  }
  unbindClickOutside() {
    if (this.attrImagePreviews.clickController) {
      this.attrImagePreviews.clickController.abort();
      this.attrImagePreviews.clickController = null;
    } else {
      document.removeEventListener('click', this.onClickOutside);
    }
  }
  removePreviewImage() {
    const previewNode = document.querySelector('.attr-image-preview');
    previewNode?.remove();
    clearTimeout(this.attrImagePreviews.timer);
  }
  createPreviewImage(image) {
    let { width, rightWidth } = this.attrImagePreviews;
    const field = image.closest('fieldset');
    let image_content = '';
    const fieldType = field?.dataset.type || '';
    if (fieldType == 'Color') {
      const { title, colorSkuField } = image.dataset;
      const colorSku = colorSkuField || '';
      const colorSkus = colorSku.split(',').filter(v => v);
      if (colorSkus.length > 0) {
        image_content = `
          <h3>${title}</h3>
          <ul>
        `;
        for (const sku of colorSkus) {
          const field = sku.split('||');
          if (field.length == 2) {
            image_content += `<li>
              <span>${field[0]}: </span>
              <span>${field[1]}</span>
            </li>`;
          }
        }
        image_content += `</ul>`;
      }
    }
    if (width + rightWidth > window.innerWidth) {
      rightWidth = (window.innerWidth - 32) / 2;
      width = rightWidth;
    }

    const previewWidth = image_content ? width + rightWidth : width;
    const { left, top, bottom, width: imageWidth } = image.getBoundingClientRect();
    const innerWidth = window.innerWidth;
    const widthCenter = left + imageWidth / 2;
    const safeGap = 8;

    const rightSpace = widthCenter + previewWidth / 2 <= innerWidth - safeGap;
    const leftSpace = widthCenter - previewWidth / 2 >= safeGap;
    const topSpace = top - width > 88;

    const topPosition = topSpace ? top - width - 22 : bottom + 16;

    let leftPosition;
    if (rightSpace && leftSpace) {
      leftPosition = widthCenter - previewWidth / 2;
    } else if (!rightSpace) {
      leftPosition = innerWidth - previewWidth - safeGap;
    } else {
      leftPosition = safeGap;
    }

    const arrowGap = 16;
    const arrowLeft = Math.min(
      Math.max(widthCenter - leftPosition, arrowGap),
      previewWidth - arrowGap
    );

    this.removePreviewImage();

    const node = `<div style="--width: ${width}px;--field-width: ${rightWidth}px;--preview-width: ${previewWidth}px;--left: ${leftPosition}px;--top: ${topPosition}px;--arrow-left: ${arrowLeft}px;" class="attr-image-preview attr-image-preview--${fieldType} ${topSpace ? 'attr-image-preview--top' : 'attr-image-preview--bottom'} ${rightSpace ? '' : 'attr-image-preview--right'} ${leftSpace ? '' : 'attr-image-preview--left'}">
      <img width="100%" height="100%" src="${image.dataset.previewImage}">
      ${image_content ? `<div class="attr-image-preview__content">${image_content}</div>` : ''}
    </div>`;

    document.body.insertAdjacentHTML('beforeend', node);

    if (!this.attrImagePreviews.isHoverDevice) {
      this.attrImagePreviews.currentNode = image;

      window.addEventListener('scroll', () => {
        this.removePreviewImage();
      }, { passive: true, once: true });

      this.bindClickClose();

      clearTimeout(this.attrImagePreviews.timer);
      this.attrImagePreviews.timer = setTimeout(() => {
        this.removePreviewImage();
      }, 5000);
    }
  }
  handlePreviewImage(imageLabel, image) {
    if (this.attrImagePreviews.isHoverDevice) {
      let timer = null;
      imageLabel.addEventListener('mouseenter', () => {
        timer = setTimeout(() => {
          this.createPreviewImage(image);
        }, 200);
      });
      imageLabel.addEventListener('mouseleave', () => {
        this.removePreviewImage();
        clearTimeout(timer);
      });
    } else {
      imageLabel.addEventListener('click', () => {
        this.createPreviewImage(image);
      })
    }
  }
  attrImagePreview() {
    if (!this.skuImage.length) return;
    for (const image of this.skuImage) {
      const previewImage = image.dataset.previewImage;
      if (!previewImage) continue;
      const imageLabel = image.closest('label');
      if (!imageLabel) return;
      this.handlePreviewImage(imageLabel, image)
    }
  }
  
  initOptions() {
    const { options } = this.defaultOptions;
    const fields = this.fields.filter(item => !(
      item.source === 1 &&
      item.values?.length === 1 &&
      item.values[0]?.displayValue === 'Default Title'
    ));
    this.assembledFields = fields.map((field, _index) => {
      const { optionKey, optionType, source, validationRules, values } = field;
      const _values = (values || []).map((value) => {
        const { valueKey, displayValue, isDefault } = value
        const selected = source == 1 ? options.includes(displayValue) : isDefault;
        return {
          ...value,
          key: valueKey,
          label: displayValue,
          optionKey,
          source,
          selected
        }
      });
      if (['text', 'number', 'date'].includes(optionType)) {
        field.current_value = validationRules?.defaultValue;
      }
      return {
        ...field,
        key: optionKey,
        values: _values,
        fieldIndex: _index
      };
    });
    for (const field of this.assembledFields) {
      this.fieldMap.set(field.key, field);
      if (field.conditionalLogic) { this.ruleMap.set(field.key, field.conditionalLogic); }
      for (const value of field.values) {
        this.fieldMap.set(value.key, value);
        if (value.conditionalLogic) { this.ruleMap.set(value.key, value.conditionalLogic); }
      }
    }
  }
  customFieldsData() {
    const fieldScript = this.querySelector('script[type="application/json"][data-custom-fields]');
    this.fields = fieldScript?.textContent ? JSON.parse(fieldScript.textContent) : [];
    fieldScript?.remove();
    const adobeScript = this.querySelector('script[type="application/json"][data-adobe-images]');
    this.adobeImages = adobeScript?.textContent ? JSON.parse(adobeScript.textContent) : [];
    adobeScript?.remove();
    this.assembledFields = [];
    this.fieldMap = new Map();
    this.ruleMap = new Map();
    this._totalPrice = 0;
    this.initOptions();
  }
  initWebComponents() {
    this.a11y.syncDisclosures();
    CustomProductA11y.syncGroupedRadios(this);
    this.updateField();
    this.attrImagePreview();
    this.handlePropertiesSubmit();
    this.previewMobileWindowImage();
    this.updateAddToCartButtonState();
  }
  updateAddToCartButtonState(initializing = false) {
    const button = this.addToCartButton;
    if (!button) return;
    if (initializing) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      return;
    }
    button.disabled = button.dataset.canAddToCart !== 'true';
    button.setAttribute('aria-busy', 'false');
  }
  initFieldEvent() {
    this.addEventListener("update-field-input", (e) => {
      const key = e.target.closest('fieldset').dataset.key;
      const value = e.target.value;
      this.updateField(key, { current_value: value });
    });
    this.addEventListener("update-field-checked", (e) => {
      const key = e.target.closest('fieldset').dataset.key;
      const value = e.target.value;
      this.updateField(key, value);
      const label = e.target.closest('fieldset').dataset.label;
      if (this.adobe_keys.includes(label)) {
        this.updateAdobeImage({});
      };
    });
    this.addEventListener("update-field-size", (e) => {
      const {
        widthInputValue,
        lengthInputValue,
        widthFractionSelectValue,
        lengthFractionSelectValue,
        widthInchesValue,
        lengthInchesValue,
        sizeUnit
      } = e.target;
      const key = e.target.closest('fieldset').dataset.key;
      this.updateField(key, {
        tps_width_value: widthInputValue,
        tps_width_fraction: widthFractionSelectValue,
        tps_length_value: lengthInputValue,
        tps_length_fraction: lengthFractionSelectValue,
        tps_width_inches: widthInchesValue,
        tps_length_inches: lengthInchesValue,
        tps_size_unit: sizeUnit
      });
    });
    this.addEventListener("quantity:update", (e) => {
      this.updateTotalPrice();
    });
    this.handleDelegatedClick();
  }
  connectedCallback() {
    this.initFieldEvent();
    this.observeAddToCart();
    this.observeMobileWindow();
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('.prodcut_gallery[data-gallery-trigger]');
      if (!trigger || !this.contains(trigger)) return;
      event.preventDefault();
      this.openSwatchGallery(trigger);
    });
    this.addEventListener('ctm:swatch-gallery-toggle', (event) => {
      if (event.target !== this || !event.detail?.variantId) return;
      event.detail.promise = AddSwatchBtn.updateCart({
        sku: event.detail.variantId,
        quantity: event.detail.action === 'remove' ? 0 : 1,
        source: 'custom-swatch-gallery',
      }).then((data) => {
        this.changeSwatchButton(data.sections);
        return data;
      });
    });
  }
  readSwatchGalleryItems() {
    const script = this.querySelector('[data-swatch-gallery-items]');
    if (!script) return [];
    try {
      const items = JSON.parse(script.textContent || '[]');
      return Array.isArray(items) ? items : [];
    } catch (error) {
      console.error('[custom-swatch-gallery] invalid gallery data', error);
      return [];
    }
  }
  openSwatchGallery(trigger) {
    const gallery = document.querySelector('#CtmSwatchGallery ctm-swatch-gallery');
    const items = this.readSwatchGalleryItems();
    if (!gallery || !items.length) return;
    gallery.open({
      items,
      activeVariantId: this.defaultOptions?.id,
      trigger,
      owner: this,
      ownerType: this.localName === 'custom-product-bundled' ? 'bundle' : 'product',
    });
  }
  changeLoadingState(state = 'add') {
    if (state === 'remove') {
      this.addToCartButton?.setAttribute('aria-busy', 'false');
      return;
    }
    this.addToCartButton?.setAttribute('aria-busy', 'true');
  }
  handleDelegatedClick() {
    this.addEventListener('click', (event) => {
      this.handleExpandedButton(event);
      this.handleNextFieldset(event);
    });
  }
  handleExpandedButton({ target }) {
    if (target.closest('.legend-question')) return;
    const expanded = target.closest('.legend-expanded[aria-expanded]');
    if (!expanded) return;
    this.a11y.toggleDisclosure(expanded, undefined, () => CustomProductA11y.syncGroupedRadios(this));
  }
  handleNextFieldset({ target }) {
    const nextBtn = target.closest('.ctm-next-btn');
    if (!nextBtn) return;
    const nextWrap = nextBtn.closest('.ctm-field-next');
    const validate = nextWrap.dataset.validate;
    const fieldset = nextWrap.closest('fieldset');
    if (validate === 'y') {
      const key = fieldset.dataset.key;
      if (!this.validateFieldRule(key)) return;
    }
    const toggleFieldset = (fieldset, expanded) => {
      const button = fieldset?.querySelector('legend > .legend-expanded');
      if (!button) return;
      const isExpanded = button.getAttribute('aria-expanded') === 'true';
      if (isExpanded !== expanded) {
        button.click();
      }
    };
    const fieldsets = Array.from(this.querySelectorAll('fieldset[data-key], fieldset[data-type]'));
    const currentIndex = fieldsets.indexOf(fieldset);
    const nextFieldset = fieldsets
      .slice(currentIndex + 1)
      .find((field) => {
        const isVariantPicker = field.closest('custom-variant-picker');
        return (!!isVariantPicker || (
          field.getAttribute('aria-disabled') === 'false' &&
          field.getAttribute('aria-hidden') === 'false'
        ));
      });
    if (!nextFieldset) return;
    toggleFieldset(fieldset, false);
    toggleFieldset(nextFieldset, true);
    this.a11y.focusFieldsetToggle(nextFieldset);
  }
  updateOptionValues(html) {
    const variantSelects = html.querySelector('custom-variant-picker');
    const variantSelectors = this.querySelector('custom-variant-picker');
    if (variantSelects) {
      Morph.morph(variantSelectors, variantSelects);
    }
  }
  updateAddToCartButton(html) {
    const addToCart = html.querySelector('.product-form__buttons');
    const _addToCart = this.querySelector('.product-form__buttons');
    const _addToCartLoading = _addToCart?.querySelector('.btn-loader');
    if (addToCart) {
      const addToCartButton = addToCart.querySelector('button[is="hover-button"].custom-add-to-cart');
      if (_addToCartLoading) {
        addToCartButton.appendChild(_addToCartLoading);
      }
      Morph.morph(_addToCart, addToCart);
      this.updateAddToCartButtonState();
    }
  }
  returnSelectValue(label) {
    const adobe_labels = {
      "wc": ["COLOR"],
      "trim": ["TRIM BORDER STYLE", "LEADING EDGE BORDER COLOR"],
      "bb": ["BOTTOM BORDER COLOR"]
    };
    const labels = adobe_labels[label] || [];
    const field = this.assembledFields.find((item) => labels.includes(item.label.toUpperCase()) && !item.hide);
    if (!field) return '';
    return field.values.find((item) => item.selected)?.label?.trim() || '';
  }
  returnSelectAbode(label) {
    const label_adobes = {
      "color": "wc"
    };
    return label_adobes[label] || '';
  }
  updateAdobeImage({ label, value }) {
    const adobeImage = this.adobeImages[0];
    if (!adobeImage) return;
    const left_image = this.querySelector('.ctm-product_lcont img');
    if (!left_image) return;
    this.mediaRevision += 1;
    const { model, param, slots = [] } = adobeImage;
    const designCode = this.querySelector('custom-product-apply-code');
    const params = [];
    let wc_src_value = '';
    const adobe_slot = this.returnSelectAbode(label);
    const pushHideSlot = (slot) => params.push(`obj=${slot}&hide=1`);
    const pushSelectedSlot = (slot, hideDuplicatedWc = false) => {
      const slot_item = adobeImage[slot];
      if (!slot_item) {
        pushHideSlot(slot);
        return;
      }
      const selectLabel = adobe_slot == slot ? value : this.returnSelectValue(slot);
      const slot_obj = slot_item[selectLabel];
      if (!slot_obj) {
        pushHideSlot(slot);
        return;
      }
      let slot_param = `obj=${slot}&src=${slot_obj.src}&${slot_obj.param}`;
      if (slot === 'wc') { wc_src_value = slot_obj.src };
      if (hideDuplicatedWc && slot !== 'wc' && wc_src_value === slot_obj.src) {
        slot_param = `obj=${slot}&hide=1`;
      }
      params.push(slot_param);
    };

    for (const slot of slots) {
      if (designCode && slot === 'wc') {
        const { fileKey, param } = designCode.designCodeData;
        fileKey ? params.push(`obj=wc&src={${fileKey}}&${param}`) : pushHideSlot(slot);
        continue;
      }
      pushSelectedSlot(slot, !designCode);
    }
    const model_param = params.join('&');
    const base_url = `${this.api_url}${model}?${model_param}&${param}`;
    const isMobile = window.matchMedia(theme.config.mediaQuerySmall).matches;
    const current_url = `${base_url}${isMobile ? '&hei=700' : '&hei=1200'}`;
    const current_url_srcset = `${base_url}&hei=700 560w,${base_url}&hei=1200 1200w`;
    left_image.src = current_url;
    left_image.srcset = current_url_srcset;
    this.updateMobileWindowImage(current_url);
  }
  updateSlideImage({ target }) {
    if (!this.adobeImages?.length) return;
    const label = target.closest('fieldset').dataset.label;
    if (this.adobe_keys.includes(label)) {
      this.updateAdobeImage({ label, value: target.value?.trim() });
    }
  }
  updateOptionByKey(key, update) {
    const item = this.fieldMap.get(key);
    if (!item) return;
    const { optionType, values = [] } = item;
    if (['text', 'number', 'date', 'size'].includes(optionType)) {
      Object.assign(item, update);
      return;
    }
    const _values = values.map((value) => {
      value.selected = update.some((item) => item.key === value.key) ? true : false;
      return value;
    });
    Object.assign(item, { values: _values });
  }
  isSelected(key) {
    let dependItem = this.fieldMap.get(key);
    if (dependItem?.selected) {
      return !this.returnRuleChain(key);
    }
    return false;
  }
  returnRuleChain(key) {
    let item = this.fieldMap.get(key);
    if (!item) return false;
    if (!item.conditionalLogic) {
      if (key !== item.optionKey) {
        item = this.fieldMap.get(item.optionKey);
        if (!item) return false;
        if (!item.conditionalLogic) return false;
      } else { return false; }
    }
    const { conditions = [], relation } = item.conditionalLogic;
    const results = conditions.map((condition) => {
      const { comparator, valueKey } = condition;
      const isSelected = this.isSelected(valueKey);
      if (comparator === 'equals') {
        return isSelected;
      } else if (comparator === 'not_equals') {
        return !isSelected;
      }
      return false;
    });
    if (relation === 'and') {
      return results.every((result) => result);
    } else if (relation === 'or') {
      return results.some((result) => result);
    }
    return false;
  }
  updateOptionShowOrHide() {
    for (const option of this.assembledFields) {
      const { conditionalLogic, values = [], key } = option;
      for (const value of values) {
        if (!value.conditionalLogic) continue;
        const match = this.returnRuleChain(value.key);
        value[value.conditionalLogic.effect] = match;
      }
      if (!conditionalLogic) continue;
      const match = this.returnRuleChain(key);
      option[conditionalLogic.effect] = match;
    }
  }
  updateTotalPrice() {
    if (!this.defaultOptions.id) return;
    const initialPrice = this.defaultOptions.price * 1;
    let totalPrice = initialPrice;
    let compareAtPrice = this.defaultOptions.compare_at_price ? this.defaultOptions.compare_at_price * 1 : 0;
    for (const option of this.assembledFields) {
      if (option.source === 1) continue;
      if (option.hide) continue;
      if (option.optionType === 'size') {
        totalPrice += this.computedSizePrice(option);
        continue;
      }
      if (option.optionType === 'number' && option.pricingRule) {
        totalPrice += this.computedNumberPrice(option);
        continue;
      }
      for (const value of option.values) {
        if (value.hide) continue;
        if (value.disable) continue;
        if (value.selected) {
          totalPrice += this.computedPrice(value.pricingRule);
        }
      }
    }
    const priceElements = this.querySelectorAll('.custom-product-template__price');
    for (const priceElement of priceElements) {
      const quantity = this.quantityInput?.value || 1;
      const totalPriceHtml = theme.Currency.formatMoney(this.priceFromCents(totalPrice * quantity), theme.settings.moneyFormat);
      if (compareAtPrice > initialPrice) {
        const savePrice = compareAtPrice - initialPrice;
        const compareAtPriceHtml = theme.Currency.formatMoney(this.priceFromCents((totalPrice + savePrice) * quantity), theme.settings.moneyFormat);
        const savePriceHtml = theme.Currency.formatMoney(this.priceFromCents(savePrice), theme.settings.moneyFormat);
        priceElement.innerHTML = `<span class="custom-product__sale">${totalPriceHtml}</span><s class="custom-product__compare">${compareAtPriceHtml}</s>`
      } else {
        priceElement.innerHTML = `<span class="custom-product__sale">${totalPriceHtml}</span>`;
      }
      priceElement.setAttribute('aria-live', 'polite');
      priceElement.setAttribute('aria-atomic', 'true');
      this._totalPrice = totalPrice;
    }
  }
  updateCustomValue() {
    for (const option of this.assembledFields) {
      if (option.hide) {
        option.custom_value = '';
        continue;
      }
      if (option.optionType === 'size') {
        option.custom_value = this.createSizeTitle(option);
        continue;
      }
      if (option.optionType === 'text' && option.current_value) {
        option.custom_value = option.current_value;
        continue;
      }
      if (option.optionType === 'date') {
        option.custom_value = option.current_value || '';
        continue;
      }
      if (option.optionType === 'number' && option.current_value) {
        const displayUnit = option?.config?.displayUnit;
        option.custom_value = `${option.current_value}${displayUnit ? displayUnit : ''}`;
        continue;
      }
      const selectedValues = option.values.filter((value) => value.selected && !value.hide && !value.disable).map((value) => value.label);
      option.custom_value = selectedValues.join(',');
    }
  }
  updateCustomOption(option) {
    const field = this.querySelector(`[data-key="${option.key}"]`);
    if (!field) return;
    field.setAttribute('aria-hidden', (option.hide || false));
    field.setAttribute('aria-disabled', (option.disable || false));
    if (field.matches('input, button, select, textarea')) {
      field.disabled = (option.disable || false);
    }
    for (const _value of (option.values || [])) {
      this.updateCustomOption(_value);
    }
  }
  updateCustomOptionTitle(option) {
    const { custom_value, key } = option;
    const field_title = this.querySelector(`fieldset[data-key="${key}"] legend span.legend-value`);
    if (!field_title) return;
    if (field_title.innerHTML === custom_value) return;
    field_title.innerHTML = custom_value || '';
  }
  updateCustomOptionSort(option) {
    const { key, sortIndex } = option;
    const field_sort = this.querySelector(`fieldset[data-key="${key}"] legend span.legend-sort`);
    if (!field_sort) return;
    let index = sortIndex + 1;
    if (this.closest(CUSTOM_PRODUCT_BUNDLED)) {
      index = index + 1;
    }
    if (field_sort.innerHTML === index) return;
    field_sort.innerHTML = index;
  }
  updateCustomOptionElement() {
    let offset = 0;
    for (const option of this.assembledFields) {
      if (option.hide) { offset = offset - 1; }
      option.sortIndex = option.fieldIndex + offset;
      if (option.source === 1) continue;
      this.updateCustomOption(option);
      this.updateCustomOptionTitle(option);
      this.updateCustomOptionSort(option);
    }
  }
  updateField(key, update) {
    this.updateOptionByKey(key, update);
    this.updateOptionShowOrHide();
    this.updateTotalPrice();
    this.updateCustomValue();
    this.updateCustomOptionElement();
  }
  fetchProductInfo(requestUrl) {
    if (!this.productCache.has(requestUrl)) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const promise = fetch(requestUrl, {
        signal: this.abortController.signal
      }).then((response) => response.text())
        .catch((err) => {
          this.productCache.delete(requestUrl);
          throw err;
        });
      this.productCache.set(requestUrl, promise);
    }
    return this.productCache
      .get(requestUrl)
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }
  renderProductInfo({ requestUrl, target }) {
    const requestId = ++this.productInfoRequestId;
    this.updateAddToCartButtonState(true);
    return this.fetchProductInfo(requestUrl).then((html) => {
      if (requestId !== this.productInfoRequestId) return;
      this.updateOptionValues(html);
      this.updateAddToCartButton(html);
      this.updateField(target.dataset.optionKey, [{
        key: target.dataset.key
      }]);
    }).catch((error) => {
      if (error.name === 'AbortError') {
        console.log('Fetch aborted by user');
      } else {
        console.error(error);
      }
    });
  }
  validateFieldRule(key) {
    if (key === 'bundle-key') {
      const designCode = this.querySelector('custom-product-apply-code');
      if (!designCode) return [{ bool: true }];
      const rule = designCode.validateRule();
      return rule.ok;
    }
    const useField = key ? this.assembledFields.filter((item) => item.optionKey === key) : this.assembledFields;
    if (useField.length === 0) return [{ bool: true }];
    const validates = useField.map((option) => {
      if (option.source === 1) return { bool: true };
      if (option.optionType === 'html') return { bool: true };
      if (option.hide) return { bool: true };
      const { optionType, key } = option;
      const fieldset = this.querySelector(`fieldset[data-key="${key}"]`);
      if (optionType === 'text') {
        const fieldText = fieldset.querySelector('custom-field-text');
        const result = fieldText.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      if (optionType === 'date') {
        const fieldDate = fieldset.querySelector('tps-field-date');
        const result = fieldDate.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      if (optionType === 'number') {
        const fieldNumber = fieldset.querySelector('custom-field-number');
        const result = fieldNumber.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      if (optionType === 'size') {
        const fieldSize = fieldset.querySelector('custom-field-size');
        let resultWidth = { ok: true };
        let resultLength = { ok: true };
        let resultFractionWidth = { ok: true };
        let resultFractionLength = { ok: true };
        resultWidth = fieldSize.validateRule('width');
        resultLength = fieldSize.validateRule('length');
        resultFractionWidth = fieldSize.fractionValidateRule('width');
        resultFractionLength = fieldSize.fractionValidateRule('length');
        return {
          field: fieldset,
          bool: resultWidth.ok && resultLength.ok && resultFractionWidth.ok && resultFractionLength.ok
        };
      }
      if (optionType === 'dropdown') {
        const fieldSelect = fieldset.querySelector('custom-field-select');
        const result = fieldSelect.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      if (optionType === 'image_swatch') {
        const fieldImage = fieldset.querySelector('custom-field-image');
        const result = fieldImage.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      if (optionType === 'button') {
        const fieldButton = fieldset.querySelector('custom-field-button');
        const result = fieldButton.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      if (optionType === 'file_upload') {
        const fieldFile = fieldset.querySelector('custom-field-upload');
        const result = fieldFile.validateRule();
        return {
          field: fieldset,
          bool: result.ok
        };
      }
      return {
        field: fieldset,
        bool: true
      };
    });
    const designCode = this.querySelector('custom-product-apply-code');
    if (designCode) {
      const rule = designCode.validateRule();
      const fieldset = designCode.closest('fieldset[data-key="bundle-key"]');
      validates.push({
        field: fieldset,
        bool: rule.ok
      });
    }

    for (const validate of validates) {
      if (validate.bool) continue;
      const validateDesignCode = validate.field.querySelector('custom-product-apply-code');
      if (validateDesignCode) {
        const validateExpanded = validateDesignCode.closest('.field-bundle__item')?.querySelector('.legend-expanded[aria-expanded]');
        if (validateExpanded) {
          this.a11y.toggleDisclosure(
            validateExpanded,
            true,
            () => CustomProductA11y.syncGroupedRadios(this)
          );
        }
      }
      const fieldButton = validate.field.querySelector('legend > .legend-expanded[aria-expanded]');
      if (!fieldButton) continue;
      this.a11y.toggleDisclosure(
        fieldButton,
        true,
        () => CustomProductA11y.syncGroupedRadios(this)
      );
    }
    return validates.every((validate) => validate.bool);
  }
  handlePropertiesSubmit() {
    let lock = false;
    this.addToCartForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      if (!this.validateFieldRule()) {
        this.changeLoadingState('remove');
        return;
      };
      const customField = this.assembledFields.filter((option) => option.source === 0);
      let id = this.defaultOptions?.id;
      if (!id) { id = this.addToCartForm.querySelector('input[name="id"]')?.value; }
      const quantity = this.quantityInput?.value || 1;
      const formData = new FormData();
      formData.append('id', id);
      formData.append('quantity', quantity);
      const descriptions = customField.filter((f) => !f.hide && f.orderNote).map((f) => f.orderNote);
      if (descriptions?.length) {
        const description = descriptions.join(' -- ');
        formData.append('properties[_description]', description);
      }
      if (this.closest(CUSTOM_PRODUCT_BUNDLED)) {
        const bundleDom = this.querySelector('fieldset[data-key="bundle-key"]');
        const bundleTitle = bundleDom.dataset.label;
        const bundleValue = bundleDom.querySelector('label input:checked')?.value;
        if (bundleTitle && bundleValue) {
          formData.append(`properties[${bundleTitle}]`, bundleValue);
          formData.append(`properties[_custom_type]`, 'bundled');
        }
        const designApplyCode = this.querySelector('custom-product-apply-code');
        if (designApplyCode && designApplyCode.designCodeData?.fileKey) {
          formData.append(`properties[Design ID]`, designApplyCode.designCodeData?.code);
        }
      }
      for (const field of customField) {
        if (field.optionType === 'html') continue;
        if (field.hide) continue;
        const label = field.orderLabel || field.label;
        const value = field.optionType === 'date'
          ? this.formatCartDateValue(field.custom_value)
          : field.custom_value;
        if (field.optionType === 'size') {
          const { tps_width_value, tps_length_value, tps_width_fraction, tps_length_fraction, tps_width_inches, tps_length_inches, tps_size_unit } = field;
          let width = `${tps_width_value}"`;
          let _width = `${tps_width_inches}"`;
          if (tps_width_fraction && !['-/-', '0/0'].includes(tps_width_fraction)) {
            width = `${tps_width_value} ${tps_width_fraction}"`;
            _width = `${tps_width_inches} ${tps_width_fraction}"`
          }
          let length = `${tps_length_value}"`;
          let _length = `${tps_length_inches}"`;
          if (tps_length_fraction && !['-/-', '0/0'].includes(tps_length_fraction)) {
            length = `${tps_length_value} ${tps_length_fraction}"`;
            _length = `${tps_length_inches} ${tps_length_fraction}"`;
          }
          if (tps_size_unit === 'cm') {
            formData.append(`properties[_inches_size_width]`, _width);
            formData.append(`properties[_inches_size_length]`, _length);
            width = `${tps_width_value}cm`;
            length = `${tps_length_value}cm`;
          }
          formData.append(`properties[Width]`, width);
          formData.append(`properties[Length]`, length);
        } else if (field.optionType === 'file_upload') {
          const file = this.querySelector(`fieldset[data-key="${field.key}"] input[type="file"]`);
          if (!file.files[0]) continue;
          formData.append(`properties[${label}]`, file.files[0]);
        } else {
          if (!value) continue;
          formData.append(`properties[${label}]`, value);
        }
      }
      const selectedKeys = this.selectedKeys.filter(s => s.source === 0);
      let _tps = selectedKeys.map(s => s.key).join(',');
      const assembledFieldSize = this.assembledFields.filter((field) => field.optionType === 'size' && !field.hide);
      if (assembledFieldSize && assembledFieldSize.length > 0) {
        const sizePrice = assembledFieldSize.reduce((p, c) => {
          return p + this.computedSizePrice(c)
        }, 0);
        _tps += `${_tps ? ',' : ''}size:${this.encrypt(sizePrice / 100, 1000)}`;
      }
      const assembledNumber = this.assembledFields.filter((field) => field.optionType === 'number' && field.pricingRule && !field.hide);
      if (assembledNumber && assembledNumber.length > 0) {
        const numberPrice = assembledNumber.reduce((p, c) => {
          return p + this.computedNumberPrice(c);
        }, 0);
        _tps += `${_tps ? ',' : ''}number:${this.encrypt(numberPrice / 100, 1000)}`;
      }
      if (_tps) {
        _tps += `,tps:${this.encrypt(this._totalPrice / 100)}`;
        formData.append('properties[_tps]', _tps);
      }
      const sku_price = this.defaultOptions.price;
      if (sku_price >= 0) {
        formData.append('properties[_sku_price]', sku_price);
      }
      const pid = this.dataset.pid;
      if (pid) {
        formData.append('properties[_pid]', pid);
      }
      formData.append('sections', 'cart-count');
      // for (const [key, value] of formData) {
      //   console.log('====formData====', key, value);
      // }
      if (lock) return;
      lock = true;
      this.changeLoadingState();
      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        body: formData
      }).then(response => {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }
        return response.json();
      }).then((parsedState) => {
        theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
          source: 'custom-product-template',
          productVariantId: id,
          cart: {
            sections: parsedState.sections
          }
        });
        document.dispatchEvent(new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { open: true }
        }));
      }).finally(() => {
        this.changeLoadingState('remove');
        lock = false;
      })
    });
  }
  updateBtnStatus() {
    const { top, height } = this.addToCartFormRect;
    const { innerHeight } = window;
    const srcollUp = top - (this.prevTop || 0) > 0;
    if (top <= 0) {
      this.addToCartForm.classList.add('add-to-cart-form__sticky');
    } else if (top > height && top < innerHeight - height) {
      this.addToCartForm.classList.remove('add-to-cart-form__sticky');
      this.isExposed = true;
    } else if (srcollUp && this.isExposed) {
      this.addToCartForm.classList.add('add-to-cart-form__sticky');
    } else {
      this.addToCartForm.classList.remove('add-to-cart-form__sticky');
    }
    this.prevTop = top;
  }
  observeAddToCart() {
    if (theme.config.mqlSmall) {
      this.updateBtnStatus();
      window.addEventListener('scroll', () => {
        if (this.ticking) return;
        requestAnimationFrame(() => {
          this.updateBtnStatus();
          this.ticking = false;
        });
        this.ticking = true;
      }, { passive: true });
    }
  }
  updateMobileWindowImage(current_url) {
    let mobileWindow = this.querySelector('.mobile-window_img img');
    const img = this.querySelector('.ctm-product_lcont img');
    mobileWindow.src = current_url || img.src;
  }
  showMobileWindow() {
    const mobileWindow = this.querySelector('.mobile-window');
    const headerSection = document.querySelector('.header-section');
    const { bottom } = this.proudctImageRect;
    const { y } = this.productBtnReact;
    if (bottom < 166 && y > 0) {
      mobileWindow.classList.add('show-2');
    } else {
      mobileWindow.classList.remove('show-2');
    }
  }
  observeMobileWindow() {
    if (theme.config.mqlSmall) {
      window.addEventListener('scroll', theme.utils.throttle(() => {
        this.showMobileWindow();
      }), false);
    }
  }
  handleMobileWindowZoom(event) {
    const zoom = event.currentTarget;
    const image = zoom.closest('.mobile-window_img')?.querySelector('img');

    if (!image) return;

    if (!this._mobileWindowPhotoswipe) {
      this._mobileWindowPhotoswipe = new PhotoSwipeLightbox({
        closeSVG: '<svg class="pswp__icn icon" stroke="currentColor" fill="none" viewBox="0 0 30 30"><path d="m7.5 22.5 15-15m-15 0 15 15"/></svg>',
        bgOpacity: 1,
        pswpModule: () => import(theme.settings.pswpModule),
        closeTitle: theme.strings.close,
        allowPanToNext: false,
        allowMouseDrag: true,
        wheelToZoom: false,
        returnFocus: true,
        zoom: false,
        arrowPrev: false,
        arrowNext: false,
        counter: false,
      });

      this._mobileWindowPhotoswipe.addFilter('thumbEl', (_thumbEl, data) => data.thumbnailElement);
      this._mobileWindowPhotoswipe.init();
    }

    this._mobileWindowPhotoswipe.loadAndOpen(0, [{
      thumbnailElement: image,
      src: image.dataset.zoomSrc || image.currentSrc || image.src,
      srcset: image.dataset.zoomSrcset || image.srcset,
      msrc: image.currentSrc || image.src,
      width: parseInt(image.dataset.zoomWidth, 10) || image.naturalWidth || parseInt(image.getAttribute('width'), 10),
      height: parseInt(image.dataset.zoomHeight, 10) || image.naturalHeight || parseInt(image.getAttribute('height'), 10),
      alt: image.alt,
      thumbCropped: true,
    }]);
  }
  previewMobileWindowImage() {
    const zoom = this.querySelector('.mobile-window_zoom');
    if (!zoom) return;
    zoom.addEventListener('click', (event) => {
      this.handleMobileWindowZoom(event)
    })
  }
  fetchSwatchData() {
    return fetch(window.Shopify.routes.root + 'cart?sections=cart-swatch-count').then((response) => {
      if (!response.ok) throw new Error('Fetch cart failed');
      return response.json();
    })
  }
  changeSwatchButton(data) {
    const count = data['cart-swatch-count'];
    const span = new DOMParser().parseFromString(count, 'text/html').querySelector('span');
    const { num, ids } = span.dataset;
    if (!num) return;
    const btns = document.querySelectorAll('add-swatch-btn');
    for (const btn of btns) { btn.setAttribute('cart-variant-ids', ids); }
  }
}
class CustomProductTemplate extends CustomProductParent {
  constructor() {
    super();
  }
  updateUrl() {
    const { id } = this.defaultOptions;
    if (!id) return;
    const url = new URL(window.location.href);
    url.searchParams.set('variant', id);
    window.history.replaceState(null, '', url);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("variant-change", (e) => {
      this.updateSlideImage(e.detail);
      this.renderProductInfo(e.detail).then(() => {
        this.updateUrl();
      })
    });
    requestAnimationFrame(() => {
      this.skeleton?.remove();
      this.customFieldsData();
      this.initWebComponents();
    })
  }
}
customElements.define('custom-product-template', CustomProductTemplate);

class CustomProductBundled extends CustomProductParent {
  constructor() {
    super();
    this.abortController = null;
    this.bundleWrap = this.querySelector('fieldset[data-type="bundle"]');
  }
  get bundle_handle() {
    let handle = new URLSearchParams(window.location.search).get('bundle');
    return this.returnHandle(handle);
  }
  get bundle_sku() {
    return new URLSearchParams(window.location.search).get('sku')
  }
  returnHandle(handle) {
    if (handle && this.querySelector(`custom-product-bundled-item input[data-handle="${handle}"]`)) {
      return handle;
    }
    const item = this.querySelector('custom-product-bundled-item input');
    return item?.dataset?.handle;
  }
  fetchProduct({ handle, sku }) {
    const requestUrl = `/products/${handle}?view=bundled${sku ? `&variant=${sku}` : ''}`;
    if (!this.productCache.has(requestUrl)) {
      this.abortController?.abort();
      this.abortController = new AbortController();
      const promise = fetch(requestUrl, {
        signal: this.abortController.signal
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Bundle product request failed: ${response.status}`);
          return response.text();
        })
        .catch(err => {
          this.productCache.delete(requestUrl);
          throw err;
        })
      this.productCache.set(requestUrl, promise);
    }
    return this.productCache
      .get(requestUrl)
      .then((html) => new DOMParser().parseFromString(html, 'text/html'));
  }
  updatePid(variantPickerDoc) {
    const pid = variantPickerDoc?.dataset?.pid;
    if (!pid) return;
    this.setAttribute('data-pid', pid);
  }
  updateUrlSku() {
    const url = new URL(window.location.href);
    const { id } = this.defaultOptions;
    if (!id) return;
    url.searchParams.set('sku', id);
    window.history.replaceState(null, '', url);
  }
  updateUrlHandle(handle) {
    if (!handle) return;
    const url = new URL(window.location.href);
    url.searchParams.delete('sku');
    url.searchParams.set('bundle', handle);
    window.history.replaceState(null, '', url);
  }
  updateBundledValue({ target, value, expandParents = true }) {
    const wrapper = target.closest('fieldset');
    const bundledValue = wrapper.querySelector('.legend-value');
    bundledValue.innerHTML = value;
    const parents = [];
    let parent = target.closest('.field-bundle__item, .field-bundle__section');
    while (parent && wrapper.contains(parent)) {
      parents.unshift(parent);
      parent = parent.parentElement?.closest(
        '.field-bundle__item, .field-bundle__section'
      );
    }
    if (expandParents) {
      parents.forEach((parent) => {
        const expanded = parent.querySelector(
          ':scope > .legend-expanded[aria-expanded]'
        );
        this.a11y.toggleDisclosure(expanded, true);
      });
    }
    CustomProductA11y.syncGroupedRadios(this);
  }
  updateVariantPickerLoading(isLoading) {
    const variantPicker = this.querySelector('.custom-bundled__content custom-variant-picker');
    if (!variantPicker) return;
    variantPicker.classList.toggle('is-loading', isLoading);
    variantPicker.toggleAttribute('inert', isLoading);
    variantPicker.setAttribute('aria-busy', String(isLoading));
  }
  initCheckedItem(handle) {
    const item = this.querySelector(`custom-product-bundled-item input[data-handle="${handle}"]`);
    if (!item) return;
    item.checked = true;
    const section = item.closest('.field-bundle__section');
    const expandParents = Boolean(section?.querySelector(
      ':scope > .legend-expanded.legend-expanded-hidden'
    ));
    this.updateBundledValue({ target: item, value: item.value, expandParents });
  }
  connectedCallback() {
    super.connectedCallback();
    const handle = this.bundle_handle;
    const sku = this.bundle_sku;
    if (!handle) return;
    this.initCheckedItem(handle);
    this.loadProduct({ handle, sku, type: 'init' });
    this.addEventListener('update-field-bundle', (event) => {
      const { target } = event.detail;
      const value = target.value;
      const handle = target.dataset.handle;
      if (!value) return;
      this.updateUrlHandle(handle);
      this.updateBundledValue({ target, value });
      this.loadProduct({ handle });
    });
    this.addEventListener("variant-change", (e) => {
      this.updateSlideImage(e.detail);
      this.renderProductInfo(e.detail).then(() => {
        this.updateUrlSku();
      })
    });
    this.addEventListener('product-apply-code-success', (e) => {
      const { code, fileKey, param } = e.detail;
      this.updateAdobeImage({});
    });
  }
  loadSlideImgs(url) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.fetchPriority = 'high';
      img.loading = 'eager';
      img.src = url;
      img.onload = () => resolve();
      img.onerror = () => resolve();
    });
  }
  upadateAdobeData(adobeData) {
    const adbodeDate = this.querySelector('.ctm-product_adobe_data');
    if (!adbodeDate) return;
    adbodeDate.replaceWith(adobeData.cloneNode(true));
  }
  updateMedia(mediaDoc, type, requestId) {
    const img = this.querySelector('.ctm-product_lcont');
    if (!img || !mediaDoc) return;
    const mediaRevision = ++this.mediaRevision;
    const currentImage = mediaDoc.querySelector('img');
    if (type == 'init' && currentImage) {
      const mobileUrl = currentImage.src;
      const pcUrl = mobileUrl.replace('hei=700', 'hei=1200');
      const url = window.innerWidth > 768 ? pcUrl : mobileUrl;
      this.loadSlideImgs(url).finally(() => {
        if (requestId !== this.bundleProductRequestId) return;
        if (mediaRevision !== this.mediaRevision) return;
        img.replaceWith(mediaDoc.cloneNode(true));
      });
    } else {
      img.replaceWith(mediaDoc.cloneNode(true));
    }
  }
  updateVariant(variantPickerDoc) {
    const bundledContent = this.querySelector('.custom-bundled__content');
    if (!bundledContent) return;
    bundledContent.replaceWith(variantPickerDoc.cloneNode(true));
  }
  updateBuyButton(buyButtonDoc) {
    const buyButton = this.querySelector('.custom-product-footer');
    if (!buyButton) return;
    buyButton.replaceWith(buyButtonDoc.cloneNode(true));
  }
  updateGallery(galleryDoc) {
    const gallery = this.querySelector('[data-swatch-gallery-items]');
    if (!gallery && !galleryDoc) return;
    if (!gallery && galleryDoc) {
      this.appendChild(galleryDoc.cloneNode(true));
    }
    if (gallery && galleryDoc) {
      gallery.replaceWith(galleryDoc.cloneNode(true));
    }
    if (gallery && !galleryDoc) {
      gallery.remove();
    }
  }
  invalidateGallery() {
    this.querySelector('[data-swatch-gallery-items]')?.remove();
    this.querySelectorAll('[data-gallery-trigger]').forEach((trigger) => {
      trigger.disabled = true;
      trigger.setAttribute('aria-disabled', 'true');
    });
    const gallery = document.querySelector('#CtmSwatchGallery ctm-swatch-gallery');
    if (gallery?.owner === this) gallery.close?.();
  }
  updateMobileWindow(mobileWindowDoc, mediaDoc) {
    const mobileWindow = this.querySelector('.mobile-window');
    if (!mobileWindow) return;
    const currentImage = mediaDoc.querySelector('img');
    if (!currentImage) return;
    const imgUrl = currentImage.src;
    mobileWindowDoc.querySelector('img').src = imgUrl;
    const newMedia = mobileWindowDoc.cloneNode(true);
    newMedia.classList.add(...mobileWindow.classList);
    mobileWindow.replaceWith(newMedia);
  }
  loadProduct({ handle, sku, type = '' }) {
    this.updateAddToCartButtonState(true);
    this.updateVariantPickerLoading(true);
    this.invalidateGallery();
    const requestId = ++this.bundleProductRequestId;
    return this.fetchProduct({ handle, sku }).then((htmlDoc) => {
      if (requestId !== this.bundleProductRequestId) return;
      const adobeData = htmlDoc.querySelector('.ctm-product_adobe_data');
      const mediaDoc = htmlDoc.querySelector('.ctm-product_lcont');
      const priceDoc = htmlDoc.querySelector('.custom-product-template__price');
      const variantPickerDoc = htmlDoc.querySelector('.custom-bundled__content');
      const buyButtonDoc = htmlDoc.querySelector('.custom-product-footer');
      const galleryDoc = htmlDoc.querySelector('[data-swatch-gallery-items]');
      const mobileWindowDoc = htmlDoc.querySelector('.mobile-window');

      if (!mediaDoc || !variantPickerDoc || !buyButtonDoc || !mobileWindowDoc) {
        throw new Error('Bundle product response is missing required content.');
      }

      this.upadateAdobeData(adobeData);
      this.updateMedia(mediaDoc, type, requestId);
      this.updatePid(variantPickerDoc);
      this.updateVariant(variantPickerDoc);
      this.updateBuyButton(buyButtonDoc);
      this.updateMobileWindow(mobileWindowDoc, mediaDoc);
      this.updateGallery(galleryDoc);
      this.customFieldsData();
      this.initWebComponents();
    }).catch((error) => {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }).finally(() => {
      if (requestId !== this.bundleProductRequestId) return;
      this.updateVariantPickerLoading(false);
    })
  }
}
customElements.define('custom-product-bundled', CustomProductBundled);

class CustomProductApplyCode extends HTMLElement {
  constructor() {
    super();
    this.applyCodeTextTimer = null;
    this.applyCode = this.querySelector('.ctm-field-code__btn');
    this.input = this.querySelector('input[name="design_code"]');
    this.onApplyCodeClick = this.onApplyCodeClick.bind(this);
    this.onDesignCodeInput = this.onDesignCodeInput.bind(this);
    this.field = this.querySelector('.ctm-field-code__content');
    this.status = this.querySelector('.ctm-field-code__status');
    this.designCodeData = {};
    this.errorId = `${this.input?.name || 'design-code'}-${Math.random().toString(36).slice(2)}-error`;
    this.a11y = new CustomProductA11y({ root: this });
  }
  get value() {
    return this.input?.value?.trim() || '';
  }
  createError(text) {
    this.error = this.a11y.showInlineError(this.input, this.field, this.errorId, text);
  }
  clearError() {
    this.a11y.clearInlineError(this.input, this.field, this.errorId);
    this.error = null;
  }
  announce(text) {
    if (!this.status) return;
    this.status.textContent = '';
    requestAnimationFrame(() => {
      this.status.textContent = text;
    });
  }
  validateRule() {
    if (!this.value) {
      const message = 'Please enter a Design ID';
      this.createError(message);
      return {
        ok: false,
        value: this.value,
        message
      }
    } else if (this.designCodeData?.code && !this.designCodeData?.fileKey) {
      const message = this.querySelector('.variant-error-tip')?.textContent || 'Code does not exist!';
      this.createError(message);
      return {
        ok: false,
        value: this.value,
        message
      }
    } else if (!this.designCodeData?.fileKey) {
      const message = 'Please click "Apply" the code';
      this.createError(message);
      return {
        ok: false,
        value: this.value,
        message
      }
    } else {
      this.clearError();
      return {
        ok: true,
        value: this.value
      }
    }
  }
  apllyCodeValidate() {
    if (!this.value) {
      const message = 'Please enter a Design ID';
      this.createError(message);
      return {
        ok: false,
        value: this.value,
        message
      }
    } else {
      this.clearError();
      return {
        ok: true,
        value: this.value
      }
    }
  }
  loadAppleyButton(state) {
    if (state === 'remove') {
      this.applyCode.removeAttribute('aria-busy');
      this.applyCode.querySelector('.ctm-btn-loader')?.remove();
      return;
    }
    this.applyCode.setAttribute('aria-busy', 'true');
    const loading = `
      <span class="ctm-btn-loader" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `;
    this.applyCode.querySelector('.ctm-btn-loader')?.remove();
    this.applyCode.insertAdjacentHTML('beforeend', loading);
  }
  changeApplyCodeText() {
    clearTimeout(this.applyCodeTextTimer);
    this.applyCode.textContent = 'Applied';

    this.applyCodeTextTimer = setTimeout(() => {
      this.applyCode.textContent = 'Apply';
      this.applyCodeTextTimer = null;
    }, 3000);
  }
  connectedCallback() {
    this.applyCode?.addEventListener('click', this.onApplyCodeClick);
    this.input?.addEventListener('input', this.onDesignCodeInput);
  }
  disconnectedCallback() {
    this.applyCode?.removeEventListener('click', this.onApplyCodeClick);
    this.input?.removeEventListener('input', this.onDesignCodeInput);
    clearTimeout(this.applyCodeTextTimer);
  }
  onDesignCodeInput() {
    this.clearError();
  }
  onApplyCodeClick(event) {
    event.preventDefault();
    const validation = this.apllyCodeValidate();
    if (!validation.ok) return;
    const busy = this.applyCode.getAttribute('aria-busy') === 'true';
    if (busy) return;
    this.loadAppleyButton('add');
    const code = validation.value;
    this.designCodeData = { code };
    theme.utils.ctmFetch('/design/prototype/getAssetByCode', {
      method: 'POST',
      json: { code }
    }).then((res) => {
      if (res.code === 200) {
        const fileKey = res.data?.fileKey;
        const param = res.data?.param;
        this.designCodeData = { code, fileKey, param };
        this.dispatchEvent(new CustomEvent('product-apply-code-success', {
          detail: {},
          bubbles: true
        }));
        this.announce('Design ID applied');
        this.changeApplyCodeText();
        return;
      }
      this.createError(res.message || 'Apply code failed');
    }).catch((error) => {
      this.createError('Apply code failed');
    }).finally(() => {
      this.loadAppleyButton('remove');
    })
  }
}
customElements.define('custom-product-apply-code', CustomProductApplyCode);

class CustomProductBundledItem extends HTMLElement {
  constructor() {
    super();
    this.bundleTooltip = null;
    this.activeTooltipTarget = null;
    this.bundleTooltipTimer = null;
    this.bundleTooltipHoverTimer = null;
    this.onChange = this.onChange.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.handleBundleTooltip = this.handleBundleTooltip.bind(this);
  }
  connectedCallback() {
    CustomProductA11y.syncGroupedRadios(this);
    this.addEventListener('change', this.onChange);
    this.addEventListener('keydown', this.onKeydown);
    ['mouseover', 'mouseout', 'focusin', 'focusout', 'click'].forEach((eventName) => {
      this.addEventListener(eventName, this.handleBundleTooltip);
    });
    const content = this.querySelector('.field-bundle__content:has(input:checked)');
    if (!content) return;
    const category = content.getAttribute('design-category');
    this.createDesignCode(category);
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('keydown', this.onKeydown);
    ['mouseover', 'mouseout', 'focusin', 'focusout', 'click'].forEach((eventName) => {
      this.removeEventListener(eventName, this.handleBundleTooltip);
    });
    this.removeBundleTooltip();
  }
  shouldIgnoreBundleEvent(event) {
    return Boolean(event.target.closest('[data-skip-bundle-events]'));
  }
  designCodeHtml(type = 'category') {
    let content = '';
    let info = `<p class="ctm-field-code__title rte">Get a Design ID from the <a href="/pages/design-library" target="_blank" aria-label="Browse Designs">Design Library &rarr;</a></p>`;

    if (type == 'partner') {
      info = '<p class="ctm-field-code__title">Enter the Design ID to apply your selected design.</p>';
      content = `
        <div class="ctm-field-item">
          <a href="/pages/design-library" target="_blank" aria-label="Browse Designs">
            <h3 class="ctm-field-item__title">Browse Fabric Library</h3>
            <div class="ctm-field-item__link">
              <p>Explore designs from our fabric partners.</p>
              <span>&rarr;</span>
            </div>
          </a>
        </div>
      `;
    }
    return `
      <custom-product-apply-code class="ctm-field-code" data-skip-bundle-events>
        ${info}
        <div class="ctm-field-code__content">
          <div class="ctm-field-code__input">
            <input type="text" name="design_code" placeholder="Design ID" aria-label="Design ID">
          </div>
          <button type="button" class="ctm-field-code__btn ctm-loading-btn">Apply</button>
          <span class="sr-only ctm-field-code__status" aria-live="polite" aria-atomic="true"></span>
        </div>
        ${content ? `<div class="ctm-field-items">${content}</div>` : ''}
      </custom-product-apply-code>
    `;
  }
  syncBundleValidation(validate = 'n') {
    const nextWrap = this.querySelector('.ctm-field-next');
    if (!nextWrap) return;
    nextWrap.dataset.validate = validate;
  }
  createDesignCode(category) {
    this.querySelector('custom-product-apply-code')?.remove();
    this.syncBundleValidation();
    if (!['design', 'partner'].includes(category)) return;
    const designCode = this.querySelector(`.field-bundle__content[design-category="${category}"]`);
    const inputCheck = designCode?.querySelector('input');
    if (!inputCheck?.checked) return;
    if (designCode.querySelector('custom-product-apply-code')) return;
    designCode.insertAdjacentHTML('beforeend', this.designCodeHtml(category));
    this.syncBundleValidation('y');
  }
  onChange(event) {
    if (this.shouldIgnoreBundleEvent(event)) return;
    CustomProductA11y.syncGroupedRadios(this);
    this.dispatchEvent(new CustomEvent('update-field-bundle', {
      detail: { target: event.target },
      bubbles: true
    }));
    const content = event.target.closest('.field-bundle__content');
    if (!content) return;
    const category = content.getAttribute('design-category');
    this.createDesignCode(category);
  }
  onKeydown(event) {
    if (this.shouldIgnoreBundleEvent(event)) return;
    CustomProductA11y.handleGroupedRadioKeydown(event);
  }
  handleBundleTooltip(event) {
    if (this.shouldIgnoreBundleEvent(event)) return;
    if (event.type === 'scroll') {
      this.removeBundleTooltip();
      return;
    }

    const target = event.target.closest?.('.ctm-field-btn[data-desc]');

    if (event.currentTarget === document) {
      if (event.target.closest('.ctm-bundle-tooltip') || this.activeTooltipTarget?.contains(event.target)) return;
      this.removeBundleTooltip();
      return;
    }

    if (theme.config.mqlSmall) {
      if (event.type !== 'click') return;
      if (!target) {
        this.removeBundleTooltip();
        return;
      }
      this.showBundleTooltip(target);
      return;
    }

    if (event.type === 'mouseover') {
      if (!target || target.contains(event.relatedTarget)) return;
      clearTimeout(this.bundleTooltipHoverTimer);
      this.bundleTooltipHoverTimer = setTimeout(() => {
        this.showBundleTooltip(target);
      }, 200);
      return;
    }

    if (event.type === 'focusin') {
      this.showBundleTooltip(target);
      return;
    }

    if (!target || target.contains?.(event.relatedTarget)) return;
    this.removeBundleTooltip();
  }
  showBundleTooltip(target) {
    if (!target?.dataset.desc) return;

    this.removeBundleTooltip();

    const gap = 15;
    const safeGap = 8;
    this.bundleTooltip = document.createElement('div');
    this.bundleTooltip.id = `bundle-tooltip-${Date.now()}`;
    this.bundleTooltip.className = 'ctm-bundle-tooltip';
    this.bundleTooltip.setAttribute('role', 'tooltip');
    this.bundleTooltip.textContent = target.dataset.desc;
    this.bundleTooltip.style.visibility = 'hidden';
    document.body.appendChild(this.bundleTooltip);

    this.activeTooltipTarget = target;
    target.setAttribute('aria-describedby', this.bundleTooltip.id);

    const boundary = target.closest('.ctm-prodcut_rcont') || target.closest('.ctm-product_right') || document.documentElement;
    const boundaryRect = boundary.getBoundingClientRect();
    const boundaryLeft = Math.max(boundaryRect.left, safeGap);
    const boundaryRight = Math.min(boundaryRect.right, window.innerWidth - safeGap);
    const boundaryWidth = Math.max(0, boundaryRight - boundaryLeft);
    const maxTooltipWidth = Math.min(320, boundaryWidth);

    const targetRect = target.getBoundingClientRect();
    if (this.bundleTooltip.offsetWidth > maxTooltipWidth) {
      this.bundleTooltip.style.width = `${maxTooltipWidth}px`;
    }
    const tooltipWidth = this.bundleTooltip.offsetWidth;
    const tooltipHeight = this.bundleTooltip.offsetHeight;
    const targetCenter = targetRect.left + targetRect.width / 2;
    const topSpace = targetRect.top - tooltipHeight - gap > safeGap;
    const centeredLeft = targetCenter - tooltipWidth / 2;
    const minLeft = boundaryLeft;
    const maxLeft = boundaryRight - tooltipWidth;
    const left = Math.min(Math.max(centeredLeft, minLeft), maxLeft);

    const top = topSpace ? targetRect.top - tooltipHeight - gap : targetRect.bottom + gap;
    const arrowGap = 16;
    const arrowLeft = Math.min(
      Math.max(targetCenter - left, arrowGap),
      tooltipWidth - arrowGap
    );

    this.bundleTooltip.style.setProperty('--tooltip-left', `${left}px`);
    this.bundleTooltip.style.setProperty('--tooltip-top', `${top}px`);
    this.bundleTooltip.style.setProperty('--arrow-left', `${arrowLeft}px`);
    this.bundleTooltip.style.visibility = 'visible';
    this.bundleTooltip.classList.toggle('ctm-bundle-tooltip--top', topSpace);
    this.bundleTooltip.classList.toggle('ctm-bundle-tooltip--bottom', !topSpace);

    requestAnimationFrame(() => {
      this.bundleTooltip?.classList.add('ctm-bundle-tooltip--visible');
    });

    if (theme.config.mqlSmall) {
      window.addEventListener('scroll', this.handleBundleTooltip, { passive: true, once: true });
      document.addEventListener('click', this.handleBundleTooltip);
      this.bundleTooltipTimer = setTimeout(() => {
        this.removeBundleTooltip();
      }, 5000);
    }
  }
  removeBundleTooltip() {
    clearTimeout(this.bundleTooltipTimer);
    clearTimeout(this.bundleTooltipHoverTimer);
    this.activeTooltipTarget?.removeAttribute('aria-describedby');
    this.activeTooltipTarget = null;
    this.bundleTooltip?.remove();
    this.bundleTooltip = null;
    this.bundleTooltipTimer = null;
    this.bundleTooltipHoverTimer = null;
    window.removeEventListener('scroll', this.handleBundleTooltip);
    document.removeEventListener('click', this.handleBundleTooltip);
  }
}
customElements.define('custom-product-bundled-item', CustomProductBundledItem);

class CustomProductQuestionList extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.currentTrigger = null;
    this._parent = this.closest('.ctm-product_temp');
    this.url = window.CUSTOM_PRODUCT_COLLAPSE_DATA_URL;
    this.lists = [];
    this.listByKey = new Map();
    this.listByText = new Map();
    this.accordionItemCount = 0;
    this.pendingPanels = new WeakMap();
    CustomProductQuestionList.instanceCount = (CustomProductQuestionList.instanceCount || 0) + 1;
    this.instanceId = `custom-product-question-${CustomProductQuestionList.instanceCount}`;
    this.handleParentClick = this.handleParentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }
  normalizeText(text = '') {
    return text
      .trim()
      .replace(/\s+/g, ' ')
      .replace(/[\n\r]/g, ' ')
      .replace(/[：]/g, ':')
      .replace(/[’']/g, "'")
      .replace(/[“”]/g, '"');
  }
  normalizeMode(mode = '') {
    return `${mode || ''}`.trim().toLowerCase();
  }
  normalizeKey(key = '') {
    return this.normalizeText(key).toLowerCase();
  }
  normalizeItem(item = {}) {
    return {
      ...item,
      key: this.normalizeKey(item.key || ''),
      text: this.normalizeText(item.text || ''),
      title: `${item.title || ''}`.trim(),
      mode: this.normalizeMode(item.mode || 'collapse'),
      children: Array.isArray(item.children) ? item.children : []
    };
  }
  buildIndexes() {
    this.listByKey = new Map();
    this.listByText = new Map();
    this.lists.forEach((item) => {
      if (item.key && !this.listByKey.has(item.key)) {
        this.listByKey.set(item.key, item);
      }
      if (item.text && !this.listByText.has(item.text)) {
        this.listByText.set(item.text, item);
      }
    });
  }
  loadQuestion() {
    if (!this.url) return;
    fetch(this.url).then(response => {
      return response.json();
    }).then(data => {
      this.lists = Array.isArray(data) ? data.map((item) => this.normalizeItem(item)) : [];
      this.buildIndexes();
    }).catch(() => {
      this.lists = [];
      this.buildIndexes();
    });
  }
  resolveEntryNode(ref = '') {
    const normalizedText = this.normalizeText(ref);
    const normalizedKey = this.normalizeKey(ref);
    if (!normalizedText && !normalizedKey) return null;
    return this.listByKey.get(normalizedKey)
      || this.listByText.get(normalizedText)
      || this.lists.find((item) => item.key === normalizedKey || item.text === normalizedText)
      || null;
  }
  findMatchedItem(key) {
    return this.resolveEntryNode(key);
  }
  resolveNode(item = null) {
    if (!item) return null;
    if (typeof item === 'string' || typeof item === 'number') {
      return this.resolveEntryNode(item);
    }
    return this.normalizeItem(item);
  }
  getChildrenNodes(item = {}) {
    const children = Array.isArray(item.children) ? item.children : [];
    return children
      .map((child) => this.resolveNode(child))
      .filter(Boolean);
  }
  getDisplayTitle(node = {}, mode = '', depth = 0) {
    if (mode === 'tree' && depth === 0 && node.asSecondTitle) {
      return `${node.asSecondTitle}`.trim();
    }
    return `${node.title || ''}`.trim();
  }
  renderHeader(item = {}) {
    const headerEl = this.shadowRoot.querySelector('#main-title');
    const title = item.title || '';
    if (headerEl) headerEl.textContent = title;
  }
  setPopupFocusable(enabled) {
    const popup = this.shadowRoot.querySelector('.popup');
    const focusables = popup?.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])') || [];
    focusables.forEach((element) => {
      if (enabled) {
        if (element.dataset.previousTabindex) {
          element.setAttribute('tabindex', element.dataset.previousTabindex);
          delete element.dataset.previousTabindex;
        } else {
          element.removeAttribute('tabindex');
        }
      } else {
        if (element.hasAttribute('tabindex')) {
          element.dataset.previousTabindex = element.getAttribute('tabindex');
        }
        element.setAttribute('tabindex', '-1');
      }
    });
  }
  renderAccordionItems(container, items, mode, depth = 0) {
    if (!container || !Array.isArray(items)) return;

    const arrowSvg = `
      <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
        <path d="M15 7.5L10 12.5L5 7.5" stroke="#666" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    `;

    items.forEach((item, index) => {
      const node = this.resolveNode(item);
      if (!node) return;
      const inheritedMode = this.normalizeMode(mode || 'collapse');
      const nodeMode = inheritedMode === 'tree' && depth === 0
        ? 'tree'
        : inheritedMode === 'single'
          ? 'single'
          : this.normalizeMode(node.mode || mode || 'collapse');
      const childNodes = this.getChildrenNodes(node);
      const itemEl = document.createElement('div');
      itemEl.className = 'accordion-item';
      itemEl.dataset.depth = `${depth}`;
      itemEl.dataset.mode = nodeMode;

      if (nodeMode === 'single') {
        itemEl.classList.add('open', 'accordion-item--single');
        itemEl.innerHTML = `
          <div class="accordion-content" aria-hidden="false">${node.content || ''}</div>
        `;
        container.appendChild(itemEl);
        return;
      }

      const displayTitle = this.getDisplayTitle(node, mode, depth);
      const showTitle = Boolean(displayTitle);
      const itemIdentifier = `${this.instanceId}-${this.accordionItemCount++}`;
      const triggerId = `${itemIdentifier}-trigger`;
      const panelId = `${itemIdentifier}-panel`;
      const isOpen = depth === 0 && index === 0;
      itemEl.classList.add(`accordion-item--${nodeMode}`);
      itemEl.innerHTML = `
        ${showTitle ? `
          <button
            type="button"
            id="${triggerId}"
            class="accordion-title"
            aria-expanded="${isOpen}"
            aria-controls="${panelId}"
          >
            ${displayTitle}
            <span class="arrow" aria-hidden="true">${arrowSvg}</span>
          </button>
        ` : ''}
        <div
          class="accordion-content"
          id="${panelId}"
          ${showTitle ? `role="region" aria-labelledby="${triggerId}"` : ''}
          aria-hidden="${showTitle ? !isOpen : false}"
          ${showTitle && !isOpen ? 'inert' : ''}
        ></div>
      `;

      const titleEl = itemEl.querySelector('.accordion-title');
      const panelEl = itemEl.querySelector(':scope > .accordion-content');
      const renderPanel = () => {
        panelEl.innerHTML = `
          ${node.content ? `<div class="accordion-content__body">${node.content}</div>` : ''}
          <div class="accordion-content__children"></div>
        `;
        if (childNodes.length) {
          this.renderAccordionItems(panelEl.querySelector('.accordion-content__children'), childNodes, nodeMode, depth + 1);
        }
      };
      if (!showTitle || isOpen) {
        renderPanel();
      } else {
        this.pendingPanels.set(itemEl, renderPanel);
      }
      if (showTitle) {
        itemEl.classList.add('has-title');
        itemEl.classList.toggle('open', isOpen);
        titleEl.addEventListener('click', () => {
          this.setAccordionItemExpanded(itemEl, !itemEl.classList.contains('open'));
        });
      } else {
        itemEl.classList.add('open');
      }
      container.appendChild(itemEl);
    });
  }
  setAccordionItemExpanded(item, expanded) {
    const trigger = item.querySelector(':scope > .accordion-title');
    const panel = item.querySelector(':scope > .accordion-content');
    if (!trigger || !panel) return;
    if (expanded && this.pendingPanels.has(item)) {
      this.pendingPanels.get(item)();
      this.pendingPanels.delete(item);
    }
    item.classList.toggle('open', expanded);
    trigger.setAttribute('aria-expanded', `${expanded}`);
    panel.setAttribute('aria-hidden', `${!expanded}`);
    panel.toggleAttribute('inert', !expanded);
  }
  renderEntry(item) {
    const container = this.shadowRoot.querySelector('.accordion');
    if (!container) return;
    const normalizedItem = this.resolveNode(item);
    if (!normalizedItem) return;

    container.innerHTML = '';
    this.accordionItemCount = 0;
    const mode = this.normalizeMode(normalizedItem.mode || 'collapse');
    const children = this.getChildrenNodes(normalizedItem);

    if (mode === 'single') {
      this.renderAccordionItems(container, children.length ? children : [normalizedItem], mode, 0);
      return;
    }

    if (children.length) {
      this.renderAccordionItems(container, children, mode, 0);
      return;
    }

    if (normalizedItem.content) {
      this.renderAccordionItems(container, [normalizedItem], 'single', 0);
    }
  }
  showPopup(item) {
    const normalizedItem = this.resolveNode(item);
    if (!normalizedItem) return;

    this.renderHeader(normalizedItem);
    this.renderEntry(normalizedItem);

    const popup = this.shadowRoot.querySelector('.popup');
    const overlay = this.shadowRoot.querySelector('.overlay');

    overlay.classList.add('active');
    popup.classList.add('active');
    this.setPopupFocusable(true);
    requestAnimationFrame(() => {
      this.shadowRoot.querySelector('.close-btn')?.focus();
    });
  }
  hidePopup() {
    const popup = this.shadowRoot.querySelector('.popup');
    const overlay = this.shadowRoot.querySelector('.overlay');

    popup.classList.remove('active');
    overlay.classList.remove('active');
    this.setPopupFocusable(false);

    if (this.currentTrigger) {
      this.currentTrigger.focus();
      this.currentTrigger = null;
    }
  }
  handleKeydown(event) {
    if (event.key === 'Escape') {
      const popup = this.shadowRoot.querySelector('.popup');
      if (!popup?.classList.contains('active')) return;
      event.preventDefault();
      this.hidePopup();
    }
  }
  render() {
    const template = document.getElementById('custom-product-collapse-popup-template');
    if (!template) return;
    const content = template.content.cloneNode(true);
    this.shadowRoot.innerHTML = '';
    this.shadowRoot.appendChild(content);

    const closeBtn = this.shadowRoot.querySelector('.close-btn');
    const overlay = this.shadowRoot.querySelector('.overlay');
    closeBtn.onclick = () => this.hidePopup();
    overlay.onclick = () => this.hidePopup();
    this.setPopupFocusable(false);
  }
  connectedCallback() {
    this._parent = this.closest('.ctm-product_temp');
    this.loadQuestion();
    this.render();
    this._parent?.addEventListener('click', this.handleParentClick);
    this.shadowRoot.addEventListener('keydown', this.handleKeydown);
  }
  disconnectedCallback() {
    this._parent?.removeEventListener('click', this.handleParentClick);
    this.shadowRoot.removeEventListener('keydown', this.handleKeydown);
  }
  handleParentClick(event) {
    const target = event.target.closest('.legend-question');
    if (!target) return;
    const lookupValue = target.dataset.key || target.dataset.text || target.textContent || '';
    if (!lookupValue) return;
    const matchedItem = this.resolveEntryNode(lookupValue);
    if (!matchedItem) return;
    event.preventDefault();
    this.currentTrigger = target;
    this.showPopup(matchedItem);
  }
}
customElements.define('custom-product-question-list', CustomProductQuestionList);

class CustomVariantPicker extends HTMLElement {
  constructor() {
    super();
    this.section_id = this.dataset.sectionId;
    this.url = this.dataset.productUrl;
  }
  get defaultOptions() {
    return Array.from(this.querySelectorAll('fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
  connectedCallback() {
    this.addEventListener('change', (event) => {
      this.variantChanged(event);
    });
  }
  disconnectedCallback() { }
  variantChanged(event) {
    this.dispatchEvent(
      new CustomEvent("variant-change", {
        detail: {
          requestUrl: this.buildRequestUrlWithParams(),
          target: event.target
        },
        bubbles: true,
        composed: true
      })
    );
  }
  buildRequestUrlWithParams() {
    const params = [];
    const isBundled = this.closest(CUSTOM_PRODUCT_BUNDLED);
    isBundled ? params.push(`view=bundled`) : params.push(`section_id=${this.section_id}`);
    if (this.defaultOptions.length) {
      params.push(`option_values=${this.defaultOptions.join(',')}`);
    }
    return `${this.url}?${params.join('&')}`;
  }
}
customElements.define('custom-variant-picker', CustomVariantPicker);

class CustomFieldText extends HTMLElement {
  constructor() {
    super();
    this.wrapper = this.closest('.custom-fields');
    this.required = this.dataset.required || false;
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    // this._parent = this.closest('fieldset').dataset.key;
    this.error = null;
  }
  get value() {
    // return [{ key: this._parent, value: this.input.value }]
    return this.input.value;
  }
  createError(text) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.error.innerHTML = text;
    this.field.appendChild(this.error);
  }
  validateRule() {
    const value = this.input.value;
    if (this.required && !value.trim()) {
      const message = 'Please fill in field';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    } else {
      this.error?.remove();
      return {
        ok: true,
        value
      }
    }
  }
  connectedCallback() {
    this.input.addEventListener('input', (event) => {
      this.dispatchEvent(new CustomEvent('update-field-input', { bubbles: true }));
    });
  }
}
customElements.define('custom-field-text', CustomFieldText);

class TpsFieldDate extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required === 'true';
    this.displayFormat = 'MM/DD/YYYY';
    this.startOffset = this.parseInteger(this.dataset.startOffset, 0);
    this.maxSelectableUnits = this.parseInteger(this.dataset.maxSelectableUnits, null);
    this.disabledWeekdays = this.parseArray(this.dataset.disabledWeekdays)
      .map((day) => Number(day))
      .filter((day) => Number.isInteger(day) && day >= 0 && day <= 6);
    this.disabledDates = new Set(this.parseArray(this.dataset.disabledDates));
    this.input = this.querySelector('input');
    this.picker = null;
    this.a11y = new CustomProductA11y({ root: this });
    this.a11y.setupDateField(this);
  }
  get value() {
    return this.input.value;
  }
  parseInteger(value, fallback) {
    if (value === undefined || value === null || value === '') return fallback;
    const parsed = parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  parseArray(value) {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      return [];
    }
  }
  flatpickrDateFormat() {
    const tokenMap = {
      YYYY: 'Y',
      YY: 'y',
      MM: 'm',
      DD: 'd'
    };
    return this.displayFormat.replace(/YYYY|YY|MM|DD/g, (token) => tokenMap[token] || token);
  }
  today() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }
  addDays(date, days) {
    const nextDate = new Date(date);
    nextDate.setDate(nextDate.getDate() + days);
    return nextDate;
  }
  formatDate(date) {
    const year = `${date.getFullYear()}`;
    const tokenMap = {
      YYYY: year,
      YY: year.slice(-2),
      MM: `${date.getMonth() + 1}`.padStart(2, '0'),
      DD: `${date.getDate()}`.padStart(2, '0')
    };
    return this.displayFormat.replace(/YYYY|YY|MM|DD/g, (token) => tokenMap[token] || token);
  }
  isDisabledDate(date) {
    return this.disabledWeekdays.includes(date.getDay()) || this.disabledDates.has(this.formatDate(date));
  }
  validateRule() {
    const value = this.input.value;
    if (this.required && !value.trim()) {
      const message = 'Please select a date';
      this.a11y.showDateError(this, message);
      return {
        ok: false,
        value,
        message
      };
    }
    this.a11y.clearDateError(this);
    return {
      ok: true,
      value
    };
  }
  updateValue() {
    this.validateRule();
    this.dispatchEvent(new CustomEvent('update-field-input', { bubbles: true }));
  }
  connectedCallback() {
    const minDate = this.addDays(this.today(), this.startOffset);
    const afterTime = this.startOffset + this.maxSelectableUnits - 1;
    const maxDate = this.addDays(this.today(), afterTime);
    const dateRange = {};
    if (this.maxSelectableUnits > 0) {
      dateRange.minDate = minDate;
      dateRange.maxDate = maxDate;
    }
    if (window.flatpickr) {
      this.picker = window.flatpickr(this.input, {
        dateFormat: this.flatpickrDateFormat(),
        ...dateRange,
        disableMobile: true,
        disable: [
          (date) => this.isDisabledDate(date)
        ],
        animate: !window.matchMedia('(prefers-reduced-motion: reduce)').matches,
        onReady: (_selectedDates, _dateString, picker) => this.a11y.connectDatePicker(this, picker),
        onOpen: () => this.a11y.setDateExpanded(this, true),
        onClose: () => this.a11y.setDateExpanded(this, false),
        onChange: () => this.updateValue()
      });
    }
  }
  disconnectedCallback() {
    this.a11y.destroyDateField(this);
    this.picker?.destroy();
  }
}
customElements.define('tps-field-date', TpsFieldDate);

class CustomFieldNumber extends HTMLElement {
  constructor() {
    super();
    this.isComposing = false;
    this.min = this.getAttribute('min') || 0;
    this.max = this.getAttribute('max');
    this.required = this.dataset.required || false;
    // this._parent = this.closest('fieldset').dataset.key;
    this.input = this.querySelector('input');
    this.field = this.closest('div');
    this.error = null;
  }
  get value() {
    // return [{ key: this._parent, value: this.input.value }];
    return this.input.value;
  }
  set value(value) {
    this.input.value = value;
  }
  createError(text) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.error.innerHTML = text;
    this.field.appendChild(this.error);
  }
  formatInput() {
    const { value } = this.validateRule();
    this.value = value;
    this.dispatchEvent(new CustomEvent('update-field-input', {
      bubbles: true
    }));
  }
  normalizeInteger(value) {
    return value.replace(/[\uFF10-\uFF19]/g, s =>
      String.fromCharCode(s.charCodeAt(0) - 0xFEE0)
    ).replace(/\D/g, '');
  }
  validateRule() {
    const value = this.normalizeInteger(this.input.value);
    let num = parseInt(value, 10);
    if (this.required && isNaN(num)) {
      const message = 'Please fill in field';
      this.createError(message);
      return {
        ok: false,
        value: '',
        message
      }
    } else if (num > 0 && (num < this.min || num > this.max)) {
      const message = `must be between ${this.min} and ${this.max}.`;
      this.createError(message);
      return {
        ok: false,
        value: num,
        message
      }
    } else {
      this.error?.remove();
      return {
        ok: true,
        value
      }
    }
  }
  connectedCallback() {
    this.input.addEventListener('compositionstart', () => {
      this.isComposing = true;
    });
    this.input.addEventListener('compositionend', (e) => {
      this.isComposing = false;
      this.formatInput();
    });
    this.input.addEventListener('input', (e) => {
      if (this.isComposing) return;
      this.formatInput();
    });
  }
}
customElements.define('custom-field-number', CustomFieldNumber);

class CustomFieldSize extends HTMLElement {
  constructor() {
    super();
    this.key = this.closest('fieldset').dataset.key;
    this.allowFraction = this.dataset.allowFraction === 'true';
    this.required = this.dataset.required || true;
    this.sizeInputs = this.querySelectorAll('.custom-size-field-input input');
    this.sizeWidth = this.querySelector('.custom-size-field-input input[data-type="width"]');
    this.sizeLength = this.querySelector('.custom-size-field-input input[data-type="length"]');
    this.widthFractionSelect = this.querySelector('custom-field-select[data-select-type="width"]');
    this.lengthFractionSelect = this.querySelector('custom-field-select[data-select-type="length"]');
    this.unitBtns = this.querySelectorAll('.custom-unit-btn');
    this.unitLabels = this.querySelectorAll('.custom-unit-label');
    this.enableCm = this.dataset.enableCm === 'true';
    this.sizeUnit = 'inches';
  }
  get sizeValidationRule() {
    return this.closest('.ctm-product_temp').fields?.find((field) => field.optionKey === this.key);
  }
  get widthInputValue() {
    return this.sizeWidth.value;
  }
  get lengthInputValue() {
    return this.sizeLength.value;
  }
  set widthInputValue(value) {
    this.sizeWidth.value = value;
  }
  set lengthInputValue(value) {
    this.sizeLength.value = value;
  }
  get widthFractionSelectValue() {
    return this.widthFractionSelect ? this.widthFractionSelect.value[0].value : '';
  }
  get lengthFractionSelectValue() {
    return this.lengthFractionSelect ? this.lengthFractionSelect.value[0].value : '';
  }
  set widthFractionSelectValue(value = '-/-') {
    this.setFractionSelectValue(this.widthFractionSelect, value);
    this.widthFractionSelect.value = value;
  }
  set lengthFractionSelectValue(value = '-/-') {
    this.setFractionSelectValue(this.lengthFractionSelect, value);
    this.lengthFractionSelect.value = value;
  }
  get widthInchesValue() {
    return this.sizeUnit === 'inches' ? this.widthInputValue : this.cmToInches(this.widthInputValue, 1).value;
  }
  get lengthInchesValue() {
    return this.sizeUnit === 'inches' ? this.lengthInputValue : this.cmToInches(this.lengthInputValue, 1).value;
  }
  normalizeInteger(value = '') {
    const normalized = String(value)
      .replace(/[０-９]/g, ch => String.fromCharCode(ch.charCodeAt(0) - 0xFEE0))
      .replace(/[eE+-]/g, '');
    if (this.sizeUnit === 'cm') {
      const cleaned = normalized.replace(/[^\d.]/g, '');
      const match = cleaned.match(/^\d+(?:\.\d)?/);
      return match ? match[0] : '';
    }
    return normalized.replace(/\D/g, '');
  }
  shouldRewriteInput(value = '') {
    const current = String(value ?? '');
    if (this.sizeUnit === 'cm') {
      return !/^\d+$|^\d+\.\d$/.test(current);
    }
    return true;
  }
  toComparableNumber(value = '') {
    const normalized = this.normalizeInteger(value);
    if (normalized === '') return NaN;
    return this.sizeUnit === 'cm'
      ? Number.parseFloat(normalized)
      : Number.parseInt(normalized, 10);
  }
  fractionToDecimal(fraction = '-/-') {
    if (!fraction || ['-/-', '0/0'].includes(fraction)) return 0;
    const [numerator, denominator] = fraction.split('/').map(Number);
    if (!numerator || !denominator) return 0;
    return numerator / denominator;
  }
  formatEighthFraction(eighths = 0) {
    const fractionMap = {
      0: '0/0',
      1: '1/8',
      2: '1/4',
      3: '3/8',
      4: '1/2',
      5: '5/8',
      6: '3/4',
      7: '7/8'
    };
    return fractionMap[eighths] || '-/-';
  }
  setFractionSelectValue(select, value = '-/-') {
    if (!select) return;
    select.querySelector('.tfs__option--selected')?.classList.remove('tfs__option--selected');
    select.querySelectorAll('.tfs__option').forEach((option) => option.setAttribute('aria-selected', 'false'));
    const option = select.querySelector(`.tfs__option[data-label="${value}"]`) || select.querySelector('.tfs__option[data-label="-/-"]');
    option?.classList.add('tfs__option--selected');
    option?.setAttribute('aria-selected', 'true');
    select.input?.setAttribute('aria-activedescendant', option?.id || '');
    select.a11y?.setActiveOption(select, option);
  }
  inchesToCm(value, fraction = '-/-', step = 0) {
    if (!value || isNaN(value)) {
      return {
        value: ''
      };
    }
    const inches = Number(value) + this.fractionToDecimal(fraction);
    let _value = Math.round(inches * 2.54 * 10) / 10;
    if (step === 0) {
      _value = Math.round(inches * 2.54);
    }
    return { value: _value };
  }
  cmToInches(value, type = 0) {
    if (!value || isNaN(value)) {
      return {
        value: '',
        fraction: '-/-'
      };
    }
    if (type === 0 || !this.allowFraction) {
      return {
        value: Math.round(Number(value) / 2.54),
        fraction: '-/-'
      };
    }
    const totalEighths = Math.round((Number(value) / 2.54) * 8);
    const integer = Math.floor(totalEighths / 8);
    const fraction = this.formatEighthFraction(totalEighths % 8);
    return {
      value: integer,
      fraction
    };
  }
  cmToInchesDecimal(value, precision = 4) {
    if (!value || isNaN(value)) return '';
    return Number((Number(value) / 2.54).toFixed(precision));
  }
  getFractionMaxOffset() {
    return this.allowFraction ? 7 / 8 : 0;
  }
  getMaxComparableValue(value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return numericValue;
    return numericValue + this.getFractionMaxOffset();
  }
  formatInchesRuleValue(value) {
    return Math.floor(Number(value));
  }
  toComparableInches(value = '') {
    const normalized = this.toComparableNumber(value);
    if (isNaN(normalized)) return NaN;
    if (this.sizeUnit !== 'cm') return normalized;
    return this.cmToInchesDecimal(normalized, 4);
  }
  formatRuleValue(value) {
    const numericValue = Number(value);
    if (isNaN(numericValue)) return value;
    return this.sizeUnit === 'cm'
      ? this.inchesToCm(numericValue, '-/-', 1).value
      : this.formatInchesRuleValue(numericValue);
  }
  switchUnitValidate() {
    const errorTipNumbers = this.querySelectorAll('.variant-error-tip span[data-number]');
    for (const num of errorTipNumbers) {
      num.textContent = this.formatRuleValue(num.dataset.number);
    }
  }
  switchUnit(newUnit) {
    const unit = newUnit.dataset.unit;
    if (newUnit.classList.contains('custom-unit-btn--active')) return;
    this.sizeUnit = unit;
    this.querySelectorAll('.custom-size-field-fraction').forEach(el => {
      el.style.display = unit === 'cm' ? 'none' : '';
    });
    this.unitLabels.forEach(label => { label.textContent = unit; });
    this.querySelector('.custom-unit-btn.custom-unit-btn--active').classList.remove('custom-unit-btn--active');
    newUnit.classList.add('custom-unit-btn--active');
    if (unit === 'cm') {
      const width = this.inchesToCm(this.widthInputValue, this.widthFractionSelectValue, 1);
      const length = this.inchesToCm(this.lengthInputValue, this.lengthFractionSelectValue, 1);
      this.widthInputValue = width.value;
      this.lengthInputValue = length.value;
      if (this.allowFraction) {
        const widthWrapper = this.widthFractionSelect.closest('.custom-size-field');
        const lengthWrapper = this.lengthFractionSelect.closest('.custom-size-field');
        this.removeFractionError(widthWrapper);
        this.removeFractionError(lengthWrapper);
      }
    } else {
      const width = this.cmToInches(this.widthInputValue, 1);
      const length = this.cmToInches(this.lengthInputValue, 1);
      this.widthInputValue = width.value;
      this.lengthInputValue = length.value;
    }
    this.switchUnitValidate();
    this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
  }
  createError(text, field) {
    let error = field.querySelector('.variant-error-tip');
    error && error.remove();
    error = document.createElement('div');
    error.className = 'variant-error-tip';
    error.innerHTML = text;
    field.appendChild(error);
  }
  removeError(field) {
    const error = field.querySelector('.variant-error-tip');
    error && error.remove();
  }
  createFractionError(text, field) {
    let error = field.querySelector('.variant-fraction-error-tip');
    error && error.remove();
    error = document.createElement('div');
    error.className = 'variant-fraction-error-tip';
    error.innerHTML = text;
    field.appendChild(error);
  }
  removeFractionError(field) {
    const error = field.querySelector('.variant-fraction-error-tip');
    error && error.remove();
  }
  connectedCallback() {
    for (const sizeInput of this.sizeInputs) {
      let isComposing = false;
      sizeInput.addEventListener('compositionstart', () => {
        isComposing = true;
      });
      sizeInput.addEventListener('compositionend', (e) => {
        isComposing = false;
        this.formatInput(e.target.dataset.type);
      });
      sizeInput.addEventListener('input', (e) => {
        if (isComposing) return;
        this.formatInput(e.target.dataset.type);
      });
    }
    this.addEventListener('update-field-size-select', ({ detail }) => {
      this.fractionValidateRule(detail?.type);
      this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
    });
    if (this.enableCm) {
      for (const unitBtn of this.unitBtns) {
        unitBtn.addEventListener('click', () => this.switchUnit(unitBtn));
      }
    }
  }
  formatInput(type) {
    const { tps_width_value, tps_length_value } = this.validateRule(type);
    if (this.shouldRewriteInput(tps_width_value)) {
      this.widthInputValue = this.toComparableNumber(tps_width_value);
    }
    if (this.shouldRewriteInput(tps_length_value)) {
      this.lengthInputValue = this.toComparableNumber(tps_length_value);
    }
    if (this.sizeUnit === 'cm' && this.allowFraction) {
      const width = this.cmToInches(this.widthInputValue, 1);
      const length = this.cmToInches(this.lengthInputValue, 1);
      this.widthFractionSelectValue = width.fraction;
      this.lengthFractionSelectValue = length.fraction;
    }
    this.dispatchEvent(new CustomEvent('update-field-size', { bubbles: true }));
  }
  validateRule(type) {
    const minWidth = Number(this.sizeWidth.dataset.min);
    const maxWidth = Number(this.sizeWidth.dataset.max);
    const minLength = Number(this.sizeLength.dataset.min);
    const maxLength = Number(this.sizeLength.dataset.max);
    const widthValue = this.sizeWidth.value;
    const lengthValue = this.sizeLength.value;

    const _width = this.toComparableInches(widthValue);
    const _length = this.toComparableInches(lengthValue);

    const widthWrapper = this.sizeWidth.closest('.custom-size-field');
    const lengthWrapper = this.sizeLength.closest('.custom-size-field');
    const baseRules = {
      width: { min: minWidth, max: this.getMaxComparableValue(maxWidth), wrapper: widthWrapper },
      length: { min: minLength, max: this.getMaxComparableValue(maxLength), wrapper: lengthWrapper },
    };
    const value = type === "width" ? _width : _length;
    const { min, max, wrapper } = baseRules[type] ?? {};
    if (isNaN(value)) {
      if (this.required) {
        const message = 'Please fill in field';
        this.createError(message, wrapper);
        return {
          ok: false,
          message,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      }
    } else if (value < min || value > max) {
      const message = `${type} must be between <span data-number="${min}">${this.formatRuleValue(min)}</span> and <span data-number="${max}">${this.formatRuleValue(max)}</span>`;
      this.createError(message, wrapper);
      return {
        ok: false,
        message,
        tps_width_value: widthValue,
        tps_length_value: lengthValue
      }
    } else {
      const { constraints = [] } = this.sizeValidationRule?.validationRules || {};
      const result = this.validate(constraints, _length, _width, type);
      if (!result.ok) {
        this.createError(result.message, wrapper);
        return {
          ok: false,
          message: result.message,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      } else {
        if (result.type === 'all') {
          this.removeError(widthWrapper);
          this.removeError(lengthWrapper);
        } else {
          this.removeError(wrapper);
        }
        return {
          ok: true,
          tps_width_value: widthValue,
          tps_length_value: lengthValue
        }
      }
    }
  }
  fractionValidateRule(type = 'width') {
    if (!this.allowFraction || this.sizeUnit === 'cm') return { ok: true };
    const widthWrapper = this.widthFractionSelect.closest('.custom-size-field');
    const lengthWrapper = this.lengthFractionSelect.closest('.custom-size-field');
    const baseRules = {
      width: { wrapper: widthWrapper, value: this.widthFractionSelectValue },
      length: { wrapper: lengthWrapper, value: this.lengthFractionSelectValue },
    };
    const { wrapper, value } = baseRules[type];
    if (value === '-/-') {
      this.createFractionError('Please select a fraction.', wrapper);
      return {
        ok: false,
        message: 'Please select a fraction.'
      }
    } else {
      this.removeFractionError(wrapper);
      return {
        ok: true
      }
    }
  }
  validate(rules, length, width, changedField) {
    const values = { length, width };
    const hitRule = rules.some((rule) => {
      const { whenOk, thenOk } = this.returnValue(rule, values);
      return whenOk && thenOk
    });
    if (hitRule) {
      return { ok: true, type: 'all' };
    }
    for (const rule of rules) {
      const { when, then, id } = rule;
      const { whenOk, thenOk, whenDim, thenDim } = this.returnValue(rule, values);
      if (changedField === thenDim && whenOk && !thenOk) {
        return {
          ok: false,
          ruleId: id,
          message: this._buildMessage(then, changedField)
        };
      }
      if (changedField === whenDim && thenOk && !whenOk) {
        return {
          ok: false,
          ruleId: id,
          message: this._buildMessage(when, changedField)
        };
      }
    }
    return { ok: true };
  }
  returnValue(rule, values) {
    const { when, then } = rule;
    const whenDim = when.dimension;
    const thenDim = then.dimension;
    const whenValue = values[whenDim];
    const thenValue = values[thenDim];
    const whenOk = this.checkCondition(when, whenValue);
    const thenOk = this.checkCondition(then, thenValue);
    return { whenOk, thenOk, thenDim, whenDim };
  }
  checkCondition(cond, val) {
    if (cond.comparator === "between") {
      const min = Number(cond.min);
      const max = this.getMaxComparableValue(cond.max);
      return val >= min && val <= max;
    }
    if (cond.comparator === "equals") {
      const value = Number(cond.value);
      return val === value;
    }
    return false;
  }
  _buildMessage(rulePart, field) {
    if (rulePart.comparator === "between") {
      const min = Number(rulePart.min);
      const max = this.getMaxComparableValue(rulePart.max);
      return `${field} must be between <span data-number="${min}">${this.formatRuleValue(min)}</span> and <span data-number="${max}">${this.formatRuleValue(max)}</span>.`;
    }
    if (rulePart.comparator === "equals") {
      const value = Number(rulePart.value);
      return `${field} must be equal to <span data-number="${value}">${this.formatRuleValue(value)}</span>.`;
    }
    return `${field} does not meet the requirements.`;
  }
}
customElements.define('custom-field-size', CustomFieldSize);

class CustomFieldSelect extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.allowMultiple = this.dataset.allowMultiple || false;
    this.selectType = this.dataset.selectType || 1;
    this.input = this.querySelector('input');
    this.optionsContainer = this.querySelector('.tfs__options');
    this.options = Array.from(this.querySelectorAll('.tfs__options li'));
    this.field = this.closest('div');
    this.error = null;
    this.activeIndex = 0;
    this.a11y = new CustomProductA11y({ root: this });
  }
  get optionsHeight() {
    return this.optionsContainer.scrollHeight;
  }
  get value() {
    const checked = this.querySelectorAll('li.tfs__option--selected');
    return [...checked].map(({ dataset }) => ({ key: dataset.key, value: dataset.label }));
  }
  set value(value) {
    this.input.value = value;
  }
  handleSelectOption() {
    this.validateRule();
    this.error?.remove();
    if (this.selectType == 1) {
      this.dispatchEvent(new CustomEvent('update-field-checked', {
        bubbles: true
      }));
    }
    if (['width', 'length'].includes(this.selectType)) {
      this.dispatchEvent(new CustomEvent('update-field-size-select', {
        detail: { type: this.selectType },
        bubbles: true
      }));
    }
  }
  createError(text) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.error.innerHTML = text;
    this.field.prepend(this.error);
  }
  validateRule() {
    const value = this.input.value;
    const valid = Array.from(this.querySelectorAll('li.tfs__option--selected')).filter(
      li => li.getAttribute('aria-disabled') !== 'true' && li.getAttribute('aria-hidden') !== 'true'
    )
    if (this.required && (!value || valid.length <= 0)) {
      const message = 'Please select in field';
      this.createError(message);
      return {
        ok: false,
        value,
        message
      }
    }
    return {
      ok: true,
      value
    }
  }
  connectedCallback() {
    this.a11y.syncCombobox(this);
    this.input.addEventListener('focus', () => {
      this.a11y.openCombobox(this);
    });
    this.input.addEventListener('keydown', (event) => {
      this.a11y.handleComboboxKeydown(this, event, () => this.handleSelectOption());
    });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.a11y.closeCombobox(this);
    });
    for (const option of this.options) {
      option.addEventListener('click', () => {
        this.a11y.selectComboboxOption(this, option, () => this.handleSelectOption());
      });
    }
  }
}
customElements.define('custom-field-select', CustomFieldSelect)

class CustomFieldCheckbox extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.allowMultiple = this.dataset.allowMultiple || false;
    this.minMultiple = this.dataset.minMultiple;
    this.maxMultiple = this.dataset.maxMultiple;
    this.field = this.closest('.custom-fields');
    this.error = null;
  }
  get value() {
    const checked = this.querySelectorAll('input:checked');
    return [...checked].map(({ value, dataset }) => ({ key: dataset.key, value }));
  }
  get checkedCount() {
    return Array.from(this.querySelectorAll('input:checked')).filter(
      input => input.getAttribute('aria-disabled') !== 'true' && input.getAttribute('aria-hidden') !== 'true'
    ).length;
  }
  createError(text, hide) {
    let timer;
    clearTimeout(timer);
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.error.innerHTML = text;
    this.field.prepend(this.error);
    if (!hide) return;
    timer = setTimeout(() => {
      this.error.remove();
    }, 3000);
  }
  validateRule() {
    if (this.required && this.checkedCount === 0) {
      const message = 'Please select at least one option';
      this.createError(message);
      return {
        ok: false,
        message
      }
    }
    if (this.allowMultiple) {
      if (this.checkedCount < this.minMultiple) {
        const message = `Please select at least ${this.minMultiple} options`;
        this.createError(message);
        return {
          ok: false,
          message
        }
      }
    }
    return {
      ok: true
    }
  }
  checkedRule() {
    this.addEventListener('change', (event) => {
      if (this.required && this.allowMultiple) {
        if (this.checkedCount > this.maxMultiple) {
          event.target.checked = false;
          this.createError(`No more than ${this.maxMultiple} at most`, true);
          return;
        }
      }
      this.error?.remove();
      this.dispatchEvent(new CustomEvent('update-field-checked', { bubbles: true }));
    });
  }
}
class CustomFieldButton extends CustomFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('custom-field-button', CustomFieldButton);
class CustomFieldImage extends CustomFieldCheckbox {
  constructor() {
    super();
  }
  connectedCallback() {
    this.checkedRule();
  }
}
customElements.define('custom-field-image', CustomFieldImage);

class CustomFieldUpload extends HTMLElement {
  constructor() {
    super();
    this.required = this.dataset.required || false;
    this.input = this.querySelector('input');
    this.field = this.closest('.custom-fields');
    this.error = null;
  }
  createError(text) {
    this.error = this.field.querySelector('.variant-error-tip');
    this.error && this.error.remove();
    this.error = document.createElement('div');
    this.error.className = 'variant-error-tip';
    this.error.innerHTML = text;
    this.field.prepend(this.error);
  }
  validateRule() {
    if (this.required && !this.input.files.length) {
      const message = 'Please upload one file';
      this.createError(message);
      return {
        ok: false,
        message
      }
    }
    return {
      ok: true
    }
  }
  connectedCallback() {
    this.input.addEventListener('change', (e) => {
      const files = e.target.files
      if (files && files.length > 0) {
        this.error?.remove();
      }
    });
  }
}
customElements.define('custom-field-upload', CustomFieldUpload);

class CustomFieldRadio extends HTMLElement {
  constructor() {
    super();
    this.field = this.closest('.custom-fields');
  }
  get value() {
    const checked = this.querySelectorAll('input:checked');
    return [...checked].map(({ value, dataset }) => ({ key: dataset.key, value }));
  }
  connectedCallback() {
    this.addEventListener('change', () => {
      this.dispatchEvent(new CustomEvent('update-field-checked', { bubbles: true }));
    });
  }
}
customElements.define('custom-field-radio', CustomFieldRadio);
class CustomQuantityInput extends HTMLElement {
  constructor() {
    super();
    this.min = this.getAttribute('min') || 1;
    this.max = this.getAttribute('max') || 999;
    this.input = this.querySelector('input');
    this.btns = this.querySelectorAll('.custom-quantity__icon');
    this.changeUnit = this.getAttribute('data-change-unit') || 0;
  }
  get value() {
    return this.input.value;
  }
  set value(value) {
    this.input.value = value;
    this.handleChangeUnit(value);
    this.dispatchEvent(new CustomEvent('quantity:update', { bubbles: true }));
  }
  handleChangeUnit(value) {
    if (this.changeUnit != 1) return;
    const unitNode = this.querySelector('.custom-quantity__unit');
    if (!unitNode) return;
    const unit = unitNode.dataset.unit;
    unitNode.innerHTML = value > 1 ? `${unit}s` : unit;
  }
  formatInput(value) {
    let num = parseInt(value.replace(/\D+/g, ''), 10);
    if (isNaN(num)) {
      this.value = this.min;
    } else if (num < this.min) {
      this.value = this.min;
    } else if (num > this.max) {
      this.value = this.max;
    } else {
      this.value = num;
    }
  }
  connectedCallback() {
    this.input.addEventListener('input', (e) => {
      this.formatInput(e.target.value);
    });
    for (const btn of this.btns) {
      btn.addEventListener('click', () => {
        const name = btn.getAttribute('name');
        const value = parseInt(this.value);
        if (name === 'plus') {
          this.value = value >= this.max ? this.max : value + 1;
        } else if (name === 'minus') {
          this.value = value <= this.min ? this.min : value - 1;
        }
      })
    }
  }
}
customElements.define('custom-quantity-input', CustomQuantityInput);

class CustomFieldColorImage extends HTMLElement {
  constructor() {
    super();
    this.product = this.closest('.ctm-product_temp');
  }
  cartUpdateUnsubscriber = undefined;
  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }
  connectedCallback() {
    if (!this.product) return;
    this.product.fetchSwatchData().then((data) => {
      this.product.changeSwatchButton(data);
    });
    this.subscribeCartUpdate();
  }
  subscribeCartUpdate() {
    this.cartUpdateUnsubscriber = theme.pubsub.subscribe(
      theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      (event) => {
        const { source, name } = event;
        if (source === 'cart-items' && name === 'remove' && this.product) {
          this.product.fetchSwatchData().then((data) => {
            this.product.changeSwatchButton(data);
          });
        }
      }
    );
  }
}
customElements.define('custom-field-color-image', CustomFieldColorImage);

class AddSwatchBtn extends HTMLElement {
  constructor() {
    super();
    this.product = document.querySelector('.ctm-product_temp');
    this.button = this.querySelector('button');
    this.sku = Number(this.dataset.sku);
    this.max = Number(theme.ctm.cartMaxSwatchItems) || 5;
    this.onButtonClick = this.handleButtonClick.bind(this);
  }
  static get observedAttributes() {
    return ['cart-variant-ids'];
  }
  static updateCart({ sku, quantity, source = 'add-swatch-btn' }) {
    const cartDrawer = document.querySelector('cart-drawer');
    let sections = 'cart-count,cart-swatch-count';
    sections = cartDrawer ? `${sections},${cartDrawer.dataset.sectionId}` : sections;
    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: { [sku]: quantity }, sections })
    }).then((response) => {
      if (!response.ok) throw new Error('Add swatch failed');
      return response.json();
    }).then((data) => {
      theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
        source,
        productVariantId: sku,
        cart: { sections: data.sections }
      });
      return data;
    });
  }
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    if (!this.button) return;
    const ids = String(newValue || '').split(',').filter(Boolean).map((v) => Number(v));
    const swatchTitle = this.dataset.swatchTitle || 'swatch';
    this.button.disabled = false;
    if (ids.includes(this.sku)) {
      this.button.textContent = 'In Cart';
      this.button.classList.add('variant-in-cart');
      this.button.setAttribute('aria-label', `Remove swatch ${swatchTitle} from cart`);
      this.button.setAttribute('aria-pressed', 'true');
    } else if (ids.length >= this.max) {
      this.button.textContent = 'Add Swatch';
      this.button.disabled = true;
      this.button.classList.remove('variant-in-cart');
      this.button.setAttribute('aria-label', 'Swatch limit reached');
      this.button.setAttribute('aria-pressed', 'false');
    } else {
      this.button.textContent = 'Add Swatch';
      this.button.classList.remove('variant-in-cart');
      this.button.setAttribute('aria-label', `Add swatch ${swatchTitle}`);
      this.button.setAttribute('aria-pressed', 'false');
    }
  }
  connectedCallback() {
    this.button?.setAttribute('type', 'button');
    this.button?.addEventListener('click', this.onButtonClick);
  }
  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onButtonClick);
  }
  handleButtonClick() {
    const sku = this.dataset.sku;
    if (!sku || !this.button || this.button.disabled || this.button.getAttribute('aria-busy') === 'true') return;
    this.changeSwatchToCart(sku, this.button);
  }
  loadingSwatchButton(state, button) {
    if (state === 'remove') {
      button.removeAttribute('aria-busy');
      button.querySelector('.ctm-btn-loader')?.remove();
      return;
    }
    button.setAttribute('aria-busy', 'true');
    const loading = `
      <span class="ctm-btn-loader" aria-hidden="true">
        <span></span>
        <span></span>
        <span></span>
      </span>
    `;
    button.querySelector('.ctm-btn-loader')?.remove();
    button.insertAdjacentHTML('beforeend', loading);
  }
  changeSwatchToCart(sku, button) {
    const quantity = button.classList.contains('variant-in-cart') ? 0 : 1;
    this.loadingSwatchButton('add', button);
    return AddSwatchBtn.updateCart({ sku, quantity }).then((data) => {
      if (this.product) {
        this.product.changeSwatchButton(data.sections);
      }
    }).finally(() => {
      this.loadingSwatchButton('remove', button);
    });
  }
}
customElements.define('add-swatch-btn', AddSwatchBtn);
